import React from "react";
import { Helmet } from "react-helmet-async";
import { 
  ArrowLeft, 
  MapPin, 
  Calendar, 
  Tag, 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown, 
  Trees, 
  Zap,
  ExternalLink,
  MessageSquare,
  Activity,
  Droplets,
  DollarSign,
  Landmark,
  ShieldAlert,
  Info,
  Users,
  Globe,
  Layers,
  History,
  ShieldCheck,
  Share2,
  Twitter,
  Linkedin,
  Copy,
  FileText,
  Check,
  CheckCircle,
  FilePenLine,
  Sliders,
  Maximize2
} from "lucide-react";
import { Button } from "./ui/button";
import { Tooltip as UITooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "./ui/tooltip";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "./ui/card";
import { Badge } from "./ui/badge";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  ReferenceLine
} from "recharts";
import { Company } from "../types";
import { cn } from "../lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { motion, AnimatePresence } from "motion/react";
import { toast } from "sonner";
import Markdown from "react-markdown";

import { useNavigate, Link } from "react-router-dom";
import { companies as localCompanies } from "../data/companies";
import { Progress } from "./ui/progress";
import { CompanyLogo } from "./CompanyLogo";
import { api, getCompanyModifiedTime } from "../lib/api";

interface TimelineEvent {
  year: number;
  title: string;
  description: string;
  url?: string;
  resolutionDate: string;
  statusText: string;
  status: "resolved" | "ongoing" | "pending";
  detail: string;
}

function getTimelineEvents(company: any): TimelineEvent[] {
  const scandals = company.scandals || [];
  const famousCompanies = [
    "Saudi Aramco", "ExxonMobil", "Chevron", "Shell", "BP", "Nestlé", "Cargill", "JBS", "Amazon", "Bayer", "Monsanto (Bayer)"
  ];
  const hasFamousScandals = famousCompanies.some(name => 
    company.companyName.toLowerCase().includes(name.toLowerCase())
  );

  if (hasFamousScandals && scandals.length > 0) {
    return scandals.map((scan: any) => {
      const title = scan.title || "";
      const year = scan.year;
      const description = scan.description || "";
      const url = scan.url;

      if (title.includes("Exxon Valdez")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "1994 (Initial Settlement) / 2008 (Supreme Court Ruling)",
          statusText: "Civil Penalties Paid",
          status: "resolved" as const,
          detail: "Exxon paid over $1B in joint federal/state settlements and settled private class-actions after multiple appellate reviews."
        };
      }
      if (title.includes("Exxon Knew")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "Ongoing Litigation (State Attorney General Suits)",
          statusText: "Active Litigation",
          status: "ongoing" as const,
          detail: "A continuing series of consumer fraud and climate damage lawsuits filed by multiple state attorneys general and municipalities."
        };
      }
      if (title.includes("Lago Agrio")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "Ongoing International Arbitration",
          statusText: "Active Dispute",
          status: "ongoing" as const,
          detail: "Following a $9.5B Ecuadorian award, legal battles shifted to international tribunals regarding liability apportionment and enforcement blockades."
        };
      }
      if (title.includes("Refinery Air Quality")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "2023 (Fines Settled & Flaring Caps Agreed)",
          statusText: "Settled with Consent Decree",
          status: "resolved" as const,
          detail: "Chevron settled regional air board enforcement cases with multi-million dollar penalties and committed to installing advanced laser leak sensors."
        };
      }
      if (title.includes("Deepwater Horizon")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "April 2016 (Consent Decree)",
          statusText: "Historic Civil Settlement",
          status: "resolved" as const,
          detail: "BP finalized a landmark $20.8B consent decree under the Clean Water Act, concluding federal and state civil damage assessments."
        };
      }
      if (title.includes("Net Zero Policy Pivot") || title.includes("Policy Pivot")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "Corporate Strategy Implemented",
          statusText: "Active Policy Shift",
          status: "ongoing" as const,
          detail: "BP reduced its targeted fossil fuel production cut to 25% from 40% by 2030, triggering shareholder and climate lobby pushback."
        };
      }
      if (title.includes("Niger Delta")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "2021 (Dutch Court Award) / 2025 (Active Remediation)",
          statusText: "Under Active Restitution",
          status: "ongoing" as const,
          detail: "Shell was ordered to pay compensation to Nigerian farmers and configure automated early-leak warning systems on trunklines."
        };
      }
      if (title.includes("Milieudefensie")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "Pending Appellate Judgment (2026)",
          statusText: "Under Judicial Appeal",
          status: "pending" as const,
          detail: "Shell appealed the landmark 45% carbon reduction mandate. Legal oral arguments concluded in 2024, with the final appeal verdict pending."
        };
      }
      if (title.includes("Roundup")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "June 2020 (Settlement Agreement)",
          statusText: "Class-Action Settlement",
          status: "resolved" as const,
          detail: "Bayer committed up to $10.9 billion to resolve current and potential future litigation concerning Roundup cancer allegations."
        };
      }
      if (title.includes("PCBs") || title.includes("Superfund PCB")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "December 2022",
          statusText: "Superfund Settlement",
          status: "resolved" as const,
          detail: "Monsanto agreed to pay hundreds of millions of dollars to resolve state and municipality PCB water contamination claims."
        };
      }
      if (title.includes("Glyphosate Litigation")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "Active Appeals & Settlements",
          statusText: "Ongoing Verdict Defense",
          status: "ongoing" as const,
          detail: "Defending multiple multi-million dollar jury awards while seeking Supreme Court guidance on product warning preemption rules."
        };
      }
      if (title.includes("Plastic Pollution")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "Recycling Targets Set (2025 / 2030)",
          statusText: "Operational Tracking",
          status: "ongoing" as const,
          detail: "Subject to continuous NGO audits. Committed to transition to 100% recyclable or reusable packaging formats by 2030."
        };
      }
      if (title.includes("Water Extraction")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "2022 (Permits Non-Renewed / Capped)",
          statusText: "Extraction Restrictions Enforced",
          status: "resolved" as const,
          detail: "Following regulatory clampdowns, Nestlé ceased bottled water extraction in several high-risk California wildfire watersheds."
        };
      }
      if (title.includes("Cerrado Deforestation") || title.includes("Cerrado")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "2025 (Enhanced Monitoring Covenants)",
          statusText: "Supply Chain Restructured",
          status: "resolved" as const,
          detail: "Cargill established satellite verification boundaries across Brazil's Cerrado savanna to intercept non-compliant soy suppliers."
        };
      }
      if (title.includes("Indigenous Land")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "Active Mediation & Land Restitution",
          statusText: "Under Active Dispute",
          status: "ongoing" as const,
          detail: "Faced NGO allegations regarding commodity sourcing from disputed territories; participating in land tenure remediation talks."
        };
      }
      if (title.includes("Cattle Laundering")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "2025 (Individual Farm Blockchain Tracking)",
          statusText: "Supply Chain Audit Ongoing",
          status: "ongoing" as const,
          detail: "JBS pledged 100% supplier transparency including indirect suppliers by utilizing blockchain cattle transport manifests."
        };
      }
      if (title.includes("Greenwashing Advertising")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "Pending Court Resolution (2026)",
          statusText: "Deceptive Marketing Suit",
          status: "pending" as const,
          detail: "New York Attorney General lawsuit over 'Net Zero by 2040' claims. Hearing dates scheduled for late 2025 / 2026."
        };
      }
      if (title.includes("Carbon Metrics") || title.includes("Underreported Carbon")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "2023 (Methodology Standardized)",
          statusText: "Emissions Reporting Adjusted",
          status: "resolved" as const,
          detail: "Expanded inventory boundary to include sub-contracted last-mile logistics fleets after shareholder-led transparency resolutions."
        };
      }
      if (title.includes("Data Center Fossil Fuel Lock")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "Active Grid Modernization Plans",
          statusText: "Infrastructure Dispute",
          status: "ongoing" as const,
          detail: "Negotiating new clean energy utility grid connections to offset massive AI compute power loads from fossil fuel sources."
        };
      }
      if (title.includes("Historical Carbon Giant")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "Long-term Carbon Audit Established",
          statusText: "Index Baseline Tracking",
          status: "resolved" as const,
          detail: "Climate Accountability Institute data verified Saudi Aramco's historic cumulative contributions since 1965, setting baseline tracking."
        };
      }
      if (title.includes("Aggressive Expansion Strategy")) {
        return {
          year,
          title,
          description,
          url,
          resolutionDate: "UN Investigations Active",
          statusText: "Under Human Rights Watch",
          status: "ongoing" as const,
          detail: "UN Special Procedures letters requested detail regarding Aramco's massive expansion plans and their compatibility with Paris Agreement warming targets."
        };
      }

      return {
        year,
        title,
        description,
        url,
        resolutionDate: year < 2020 ? `${year + 2} (Fines Settled)` : "Compliance Audit Ongoing",
        statusText: year < 2020 ? "Legally Settled" : "Pending Oversight Review",
        status: year < 2020 ? ("resolved" as const) : ("pending" as const),
        detail: "Standard remediation protocols enforced under regional environmental board oversight. Operational metrics regularly audited."
      };
    });
  }

  const companyId = company.id || 42;
  const sector = company.sector || "Manufacturing";
  const region = company.region || "Global";
  const country1 = (company.locations && company.locations[0]?.country) || region;
  const country2 = (company.locations && company.locations[1]?.country) || region;
  const country3 = (company.locations && company.locations[2]?.country) || "International Waters";

  const totalFines = company.finesPaid || 25000000;
  const formattedFine1 = `$${((totalFines * 0.15) / 1000000).toFixed(1)}M USD`;
  const formattedFine2 = `$${((totalFines * 0.35) / 1000000).toFixed(1)}M USD`;
  const formattedFine3 = `$${((totalFines * 0.50) / 1000000).toFixed(1)}M USD`;

  const totalToxic = company.toxicWaste || 2000;
  const formattedToxic1 = `${Math.round(totalToxic * 0.25).toLocaleString()} tons`;

  const totalDeforest = company.deforestationHectares || 1200;
  const formattedDeforest = `${Math.round(totalDeforest * 0.60).toLocaleString()} hectares`;

  // A simple deterministic pseudo-random number generator based on the company's name and ID
  const seedString = (company.companyName || "") + (company.id || 0);
  let hash = 0;
  for (let i = 0; i < seedString.length; i++) {
    hash = seedString.charCodeAt(i) + ((hash << 5) - hash);
  }
  const getSeededValue = (offset: number) => {
    const val = Math.abs(Math.sin(hash + offset)) * 10000;
    return val - Math.floor(val); // returns 0 to 1
  };

  const year1 = 2013 + Math.floor(getSeededValue(1.1) * 4); // 2013-2016
  const year2 = 2018 + Math.floor(getSeededValue(1.2) * 4); // 2018-2021
  const year3 = 2022 + Math.floor(getSeededValue(1.3) * 4); // 2022-2025

  const events: TimelineEvent[] = [];

  // Determine index for template choice
  const tIdx1 = getSeededValue(2.1) < 0.5 ? 0 : 1;
  const tIdx2 = getSeededValue(2.2) < 0.5 ? 0 : 1;
  const tIdx3 = getSeededValue(2.3) < 0.5 ? 0 : 1;

  if (sector === "Oil & Gas") {
    if (tIdx1 === 0) {
      events.push({
        year: year1,
        title: "Pipeline Integrity Leakage",
        description: `An unexpected integrity breach in a regional pipeline led to an unpermitted release of heavy hydrocarbons into local waterways near ${country1}.`,
        resolutionDate: `${year1 + 2} (Remediation Certified)`,
        statusText: "Soil Cleanup Certified",
        status: "resolved",
        detail: `Paid a settlement fine of ${formattedFine1} and successfully completed automated soil flushing and aquatic monitoring.`
      });
    } else {
      events.push({
        year: year1,
        title: "Offshore Platform Discharge",
        description: `Inspectors logged unauthorized drilling fluid discharge exceeding statutory safety margins during deepwater operations off the coast of ${country1}.`,
        resolutionDate: `${year1 + 1} (Fines Settled)`,
        statusText: "Fines Settled & Closed",
        status: "resolved",
        detail: `Paid administrative penalties of ${formattedFine1} to coastal management authorities and updated marine safety protocols.`
      });
    }

    if (tIdx2 === 0) {
      events.push({
        year: year2,
        title: "Methane Venting Telemetry Anomalies",
        description: `Independent satellite radar tracking flagged unauthorized venting and unpermitted flaring episodes at their extraction zones in ${country2}.`,
        resolutionDate: `${year2 + 2} (Audit Closed)`,
        statusText: "Compliance Restored",
        status: "resolved",
        detail: `Paid fines of ${formattedFine2} and installed automated vapor recovery units on all active production manifolds.`
      });
    } else {
      events.push({
        year: year2,
        title: "Refinery Air Quality Violations",
        description: `Sulfur dioxide emissions exceeded permitted daily thresholds at their primary processing facility in ${country2} during maintenance startups.`,
        resolutionDate: `${year2 + 1} (Consent Decree)`,
        statusText: "Fines Paid & Upgraded",
        status: "resolved",
        detail: `Agreed to a consent decree including ${formattedFine2} in civil penalties and committed to installing advanced flue-gas scrubbers.`
      });
    }

    if (tIdx3 === 0) {
      events.push({
        year: year3,
        title: "Deepwater Well Integrity Challenge",
        description: `Regulatory agencies in ${country3} initiated an active inquiry into gas pressure anomalies detected at a deepwater exploration well.`,
        resolutionDate: "Under Active Review",
        statusText: "Oversight Investigation",
        status: "ongoing",
        detail: `Defending an ongoing probe with a proposed penalty of ${formattedFine3} and undertaking third-party wellhead integrity audits.`
      });
    } else {
      events.push({
        year: year3,
        title: "Unpermitted Flaring Spikes",
        description: `Fenceline optical gas sensors in ${country3} documented sulfur and nitrogen oxides emissions spikes 150% above clean air guidelines during a process upset.`,
        resolutionDate: "Negotiating Consent Decree",
        statusText: "Ongoing Enforcement",
        status: "ongoing",
        detail: `The state environmental protection board has proposed a civil penalty of ${formattedFine3}, with consent decree terms currently being negotiated.`
      });
    }
  } else if (sector === "Mining") {
    if (tIdx1 === 0) {
      events.push({
        year: year1,
        title: "Tailings Pond Seepage Incident",
        description: `Inspectors detected elevated heavy metal discharge and acidic seepage bypassing the containment lining of a waste tailings reservoir in ${country1}.`,
        resolutionDate: `${year1 + 3} (Containment Sealed)`,
        statusText: "Containment Remediation Complete",
        status: "resolved",
        detail: `Paid ${formattedFine1} to regional water boards and constructed a thick bentonite-clay slurry wall to block further ground runoff.`
      });
    } else {
      events.push({
        year: year1,
        title: "Unpermitted Mineral Extraction Runoff",
        description: `Intense precipitation caused unchecked run-off of mineral-heavy silt from active excavation zones into downstream public watersheds in ${country1}.`,
        resolutionDate: `${year1 + 2} (Sediment Sinks Built)`,
        statusText: "Runoff Controls Approved",
        status: "resolved",
        detail: `Paid ${formattedFine1} in clean water fines and installed progressive sedimentation holding ponds around the active face.`
      });
    }

    if (tIdx2 === 0) {
      events.push({
        year: year2,
        title: "Unpermitted Forest Encroachment",
        description: `Excavators cleared and graded land outside approved operational mining lease boundaries, directly infringing on critical conservation ecosystems in ${country2}.`,
        resolutionDate: `${year2 + 1} (Offset Agreement Executed)`,
        statusText: "Legally Settled",
        status: "resolved",
        detail: `Ceased work in buffer zones, committed to a ${formattedFine2} ecological offset fund, and initiated a 10-year native planting covenant.`
      });
    } else {
      events.push({
        year: year2,
        title: "Heavy Metal Leaching Fine",
        description: `Runoff assays downstream of active open pits in ${country2} demonstrated zinc and lead concentrations exceeding safe environmental limits.`,
        resolutionDate: `${year2 + 2} (Filtration Active)`,
        statusText: "Heavy Metals Contained",
        status: "resolved",
        detail: `Settled environmental charges for ${formattedFine2} and constructed an active water-neutralizing lagoon system.`
      });
    }

    if (tIdx3 === 0) {
      events.push({
        year: year3,
        title: "Acid Mine Drainage Cleanup Directive",
        description: `Federal protection officers issued a formal citation regarding acidic mineral-heavy runoff leaking from older, inactive open-cut mine faces in ${country3}.`,
        resolutionDate: "Compliance Plan Pending",
        statusText: "Active Remediation Order",
        status: "pending",
        detail: `The company has been directed to design and construct an active lime-neutralization treatment facility estimated at ${formattedFine3}.`
      });
    } else {
      events.push({
        year: year3,
        title: "Ecosystem Fragility Violation",
        description: `Regulators in ${country3} initiated a formal injunction over exploration drilling conducted in highly sensitive wetland buffers home to protected native species.`,
        resolutionDate: "Suspension Order Active",
        statusText: "Drilling Frozen",
        status: "ongoing",
        detail: `Operations at the site have been suspended pending an environmental impact assessment; facing a proposed fine of ${formattedFine3}.`
      });
    }
  } else if (sector === "Utilities") {
    if (tIdx1 === 0) {
      events.push({
        year: year1,
        title: "Thermal Effluent Discharge Violation",
        description: `High-temperature cooling water discharge into regional river systems exceeded maximum statutory temperature thresholds, causing local fish mortality in ${country1}.`,
        resolutionDate: `${year1 + 2} (Cooling Loop Installed)`,
        statusText: "Resolved & Closed",
        status: "resolved",
        detail: `Constructed auxiliary mechanical draft cooling towers and funded a ${formattedFine1} river restoration and restocking project.`
      });
    } else {
      events.push({
        year: year1,
        title: "Fly Ash Pond Containment Leak",
        description: `Groundwater monitoring near historical ash containment areas in ${country1} revealed trace concentrations of heavy metals bypassing safety barriers.`,
        resolutionDate: `${year1 + 3} (Seepage Capped)`,
        statusText: "Seepage Mitigated",
        status: "resolved",
        detail: `Excavated ${formattedToxic1} of coal ash to double-lined dry modern storage cells and settled state environmental claims for ${formattedFine1}.`
      });
    }

    if (tIdx2 === 0) {
      events.push({
        year: year2,
        title: "Coal Ash Landfill Leakage",
        description: `Prolonged rain events compromised the secondary clay lining of an ash repository, leading to groundwater contamination with heavy metals in ${country2}.`,
        resolutionDate: `${year2 + 2} (Disposal Verified)`,
        statusText: "Excavation Complete",
        status: "resolved",
        detail: `Excavated over ${formattedToxic1} of contaminated ash to a dry, double-lined modern containment landfill. Paid ${formattedFine2} in administrative penalties.`
      });
    } else {
      events.push({
        year: year2,
        title: "Greenhouse Gas Emissions Cap Overrun",
        description: `Regulatory accounting determined that regional power plants in ${country2} exceeded the allowable seasonal carbon cap during peak demand grids.`,
        resolutionDate: `${year2 + 1} (Emission Credits Retired)`,
        statusText: "Audit Reconciled",
        status: "resolved",
        detail: `Compelled to purchase retroactive offset credits costing ${formattedFine2} and accepted an accelerated timeline for fossil-fuel decommissioning.`
      });
    }

    if (tIdx3 === 0) {
      events.push({
        year: year3,
        title: "Emissions Credit Deficit Probe",
        description: `Regional carbon cap-and-trade administrators uncovered reporting discrepancies resulting in an unhedged carbon emissions deficit during the last audit cycle in ${country3}.`,
        resolutionDate: "Audit Reconciliation Ongoing",
        statusText: "Under Regulatory Probe",
        status: "ongoing",
        detail: `Subject to retroactive carbon credit purchase requirements estimated at ${formattedFine3} and strict independent emissions monitoring.`
      });
    } else {
      events.push({
        year: year3,
        title: "Thermal River Plume Encroachment",
        description: `Aerial thermal imaging in ${country3} identified a heat plume extending further into regional lakes than allowed by their operating license, affecting fish species.`,
        resolutionDate: "Abatement Directive Issued",
        statusText: "Thermal Encroachment Dispute",
        status: "ongoing",
        detail: `Ordered to construct auxiliary cooling loops or reduce power production by 20% during summer months. Settlement talks involve a proposed ${formattedFine3} penalty.`
      });
    }
  } else if (sector === "Agriculture") {
    if (tIdx1 === 0) {
      events.push({
        year: year1,
        title: "Aquifer Depletion & Water Infraction",
        description: `Pumping stations withdrew groundwater volumes exceeding municipal emergency conservation allotments during a regional drought in ${country1}.`,
        resolutionDate: `${year1 + 2} (Pumping Cap Imposed)`,
        statusText: "Usage Capped",
        status: "resolved",
        detail: `Settled with regional water boards by accepting a permanent 30% reduction in groundwater extraction and paying a ${formattedFine1} penalty.`
      });
    } else {
      events.push({
        year: year1,
        title: "Pesticide Runoff Watershed Encroachment",
        description: `Runoff from agricultural test fields in ${country1} caused elevated synthetic compound concentrations in surrounding public irrigation canals.`,
        resolutionDate: `${year1 + 2} (Buffer Zones Built)`,
        statusText: "Runoff Controls Approved",
        status: "resolved",
        detail: `Settled civil class actions for ${formattedFine1} and implemented satellite-monitored precision buffer zones.`
      });
    }

    if (tIdx2 === 0) {
      events.push({
        year: year2,
        title: "Supply Chain Rainforest Deforestation",
        description: `Independent radar tracking traced agricultural feedstock sourcing back to illegally deforested tropical rainforest parcels in ${country2}.`,
        resolutionDate: `${year2 + 1} (Supplier Blocklisted)`,
        statusText: "Supply Chain Restructured",
        status: "resolved",
        detail: `Suspended operations with non-compliant growers and implemented a satellite-verified, zero-deforestation farm registration framework covering ${formattedDeforest}.`
      });
    } else {
      events.push({
        year: year2,
        title: "Agrarian Drift Herbicide Litigations",
        description: `Widespread civil actions were brought in ${country2} by neighbouring organic farming collectives over drift damage from proprietary chemical spray treatments.`,
        resolutionDate: `${year2 + 2} (Class Settlement)`,
        statusText: "Claims Settled",
        status: "resolved",
        detail: `Settled claims for ${formattedFine2} and implemented GPS-guided precision spray nozzles restricted to low-wind periods.`
      });
    }

    if (tIdx3 === 0) {
      events.push({
        year: year3,
        title: "Pesticide Spray Drift Litigation",
        description: `A civil lawsuit was initiated in ${country3} following widespread aerial pesticide drift that contaminated organic crops and public waterways in surrounding agrarian communities.`,
        resolutionDate: "Pending Court Verdict",
        statusText: "Class-Action Active",
        status: "ongoing",
        detail: `Defending multi-district litigation with claims exceeding ${formattedFine3}. Seeking regulatory approval for modified nozzle and wind-drift safeguards.`
      });
    } else {
      events.push({
        year: year3,
        title: "Supply Chain Forest Encroachment Suit",
        description: `NGO watchdogs initiated a legal challenge in ${country3} alleging active supply chain sourcing from newly cleared tropical rainforest tracts.`,
        resolutionDate: "Regulatory Investigation Active",
        statusText: "Sourcing Audit Underway",
        status: "ongoing",
        detail: `Cooperating with regulatory investigations, with potential supplier blocklists and compliance upgrades estimated to cost ${formattedFine3}.`
      });
    }
  } else if (sector === "Chemicals" || sector === "Pharmaceuticals") {
    if (tIdx1 === 0) {
      events.push({
        year: year1,
        title: "Hazardous Solvent Discharge",
        description: `An industrial treatment equipment rupture led to the discharge of chlorinated solvents into local municipal sanitary systems in ${country1}.`,
        resolutionDate: `${year1 + 2} (System Redesigned)`,
        statusText: "Compliance Certified",
        status: "resolved",
        detail: `Paid municipal utility damages of ${formattedFine1} and replaced single-wall storage cells with double-contained jacketed tanks.`
      });
    } else {
      events.push({
        year: year1,
        title: "Chlorinated Solvent Vapor Leak",
        description: `Fugitive emission monitors registered elevated ambient concentrations of halogenated solvents near a primary production line in ${country1}.`,
        resolutionDate: `${year1 + 2} (Thermal Oxidizers Installed)`,
        statusText: "Air Standards Met",
        status: "resolved",
        detail: `Commissioned custom thermal oxidizer equipment at a cost of ${formattedFine1} to destroy volatile compounds before chimney discharge.`
      });
    }

    if (tIdx2 === 0) {
      events.push({
        year: year2,
        title: "Unmanifested Toxic Byproduct Storage",
        description: `A regional regulatory safety inspection uncovered stockpiles of toxic waste drums stored on-site without federally mandated transport manifests and safety sheets in ${country2}.`,
        resolutionDate: `${year2 + 2} (Disposal Audited)`,
        statusText: "Fines Paid & Cleared",
        status: "resolved",
        detail: `Contracted certified high-temperature incinerators to dispose of ${formattedToxic1} of toxic slurry. Settled record-keeping infractions for ${formattedFine2}.`
      });
    } else {
      events.push({
        year: year2,
        title: "PFAS Water Contamination Case",
        description: `Groundwater assays near a legacy fluoropolymer processing plant in ${country2} detected persistent polyfluoroalkyl substances exceeding safety limits.`,
        resolutionDate: `${year2 + 3} (Filter Grid Completed)`,
        statusText: "Drinking Water Cleaned",
        status: "resolved",
        detail: `Paid ${formattedFine2} to local public utilities to install advanced granulated carbon water filtration arrays.`
      });
    }

    if (tIdx3 === 0) {
      events.push({
        year: year3,
        title: "Volatile Organic Compound Emissions Probe",
        description: `Fugitive air monitoring sensors at plant fence lines detected benzene and chemical vapor leaks exceeding safe workplace and residential limits in ${country3}.`,
        resolutionDate: "Consent Order Proposed",
        statusText: "Awaiting Court Decree",
        status: "pending",
        detail: `Proposing a comprehensive thermal oxidizer replacement cycle and funding a local public health monitoring program valued at ${formattedFine3}.`
      });
    } else {
      events.push({
        year: year3,
        title: "Toxic Chemical Discharge Litigation",
        description: `A multi-district civil lawsuit was initiated in ${country3} alleging that deepwell waste injections compromised sub-surface aquifers used for regional drinking water.`,
        resolutionDate: "Active Litigation",
        statusText: "Aquifer Suit Pending",
        status: "ongoing",
        detail: `The company is actively defending the litigation while funding emergency drinking water supplies for the community, with liabilities projected at ${formattedFine3}.`
      });
    }
  } else if (sector === "Manufacturing" || sector === "Automotive") {
    if (tIdx1 === 0) {
      events.push({
        year: year1,
        title: "Acidic Wastewater Discharge Fine",
        description: `Wastewater discharged into municipal drainage networks failed pre-treatment neutralization targets, exhibiting highly corrosive acidity levels in ${country1}.`,
        resolutionDate: `${year1 + 2} (Neutralizer Operational)`,
        statusText: "Resolved & Closed",
        status: "resolved",
        detail: `Reimbursed municipal pipeline repairs for ${formattedFine1} and commissioned an automated, continuous pH monitoring and dosing system.`
      });
    } else {
      events.push({
        year: year1,
        title: "Industrial Wastewater Acidity Spikes",
        description: `Wastewater released into sanitary systems in ${country1} failed municipal neutralization standards, causing corrosive wear on municipal main infrastructure.`,
        resolutionDate: `${year1 + 2} (pH Controllers Installed)`,
        statusText: "Acidity Mitigated",
        status: "resolved",
        detail: `Paid ${formattedFine1} in municipal system damage compensation and implemented automated continuous digital pH neutralizing controllers.`
      });
    }

    if (tIdx2 === 0) {
      events.push({
        year: year2,
        title: "Volatile Paint Solvent Over-emissions",
        description: `An EPA audit confirmed that secondary solvent extraction and recycling systems failed to achieve the mandatory 95% efficiency threshold for several fiscal quarters in ${country2}.`,
        resolutionDate: `${year2 + 2} (Fines Settled)`,
        statusText: "Compliance Restored",
        status: "resolved",
        detail: `Replaced secondary carbon absorption beds with high-efficiency catalytic systems and settled the clean air infraction for ${formattedFine2}.`
      });
    } else {
      events.push({
        year: year2,
        title: "Wastewater Heavy Metal Spikes",
        description: `Effluent tests at a key electroplating complex in ${country2} recorded cadmium and chromium concentrations exceeding statutory sewer boundaries.`,
        resolutionDate: `${year2 + 2} (Membranes Operational)`,
        statusText: "Sewer Discharge Approved",
        status: "resolved",
        detail: `Paid a fine of ${formattedFine2} and implemented an advanced membrane filtration system.`
      });
    }

    if (tIdx3 === 0) {
      events.push({
        year: year3,
        title: "Supply Chain Emissions Double-Counting Probe",
        description: `An investigation was launched regarding marketing assertions of '100% Carbon Neutral Operations' that allegedly relied on carbon offset credits that were already retired in ${country3}.`,
        resolutionDate: "Auditing Phase Underway",
        statusText: "Under Active Audit",
        status: "ongoing",
        detail: `Cooperating with regional consumer protection bureaus. A draft consent agreement requires retraining marketing divisions and paying a potential ${formattedFine3} fine.`
      });
    } else {
      events.push({
        year: year3,
        title: "Greenwashing Advertising Trial",
        description: `State attorneys general filed a consumer fraud suit in ${country3} regarding '100% Zero-Carbon Manufacturing' claims that allegedly hid massive sub-contracted metal smelting carbon footprints.`,
        resolutionDate: "Scheduled for Trial",
        statusText: "Active Fraud Action",
        status: "ongoing",
        detail: `The case is scheduled for trial, with state prosecutors seeking ${formattedFine3} in civil penalties and a comprehensive marketing overhaul.`
      });
    }
  } else if (sector === "Technology" || sector === "Finance") {
    if (tIdx1 === 0) {
      events.push({
        year: year1,
        title: "Hazardous E-Waste Sourcing Infraction",
        description: `Corporate audit trails revealed obsolete electronic hardware was transferred to unregistered waste processors in developing countries with poor safety standards.`,
        resolutionDate: `${year1 + 3} (Certified Recyclers Retained)`,
        statusText: "Compliance Restored",
        status: "resolved",
        detail: `Terminated broker agreements and implemented a strict, certified 'cradle-to-grave' recycling manifest to guarantee zero landfill exports.`
      });
    } else {
      events.push({
        year: year1,
        title: "Sub-contracted Server Power Footprint",
        description: `An independent carbon audit exposed underreporting of global electricity loads by excluding third-party computing clusters in ${country1}.`,
        resolutionDate: `${year1 + 2} (Ledger Reconciled)`,
        statusText: "Carbon Disclosures Approved",
        status: "resolved",
        detail: `Revised international greenhouse gas disclosures to incorporate all leased computing infrastructure and paid an administrative reconciliation fee of ${formattedFine1}.`
      });
    }

    if (tIdx2 === 0) {
      events.push({
        year: year2,
        title: "Scope 3 Logistics Carbon Omission",
        description: `An independent research consortium exposed major underreporting in greenhouse gas metrics by excluding third-party delivery transport and silicon fabrication chains.`,
        resolutionDate: `${year2 + 2} (Methodology Adjusted)`,
        statusText: "ESG Disclosures Revised",
        status: "resolved",
        detail: `Revised global carbon disclosures to incorporate sub-contracted logistics networks, which increased reported emissions intensity by 45%.`
      });
    } else {
      events.push({
        year: year2,
        title: "Cryptographic Center Grid Strain",
        description: `Local energy boards in ${country2} issued citations regarding grid congestion and fossil-fuel reliance at their high-intensity computing facilities.`,
        resolutionDate: `${year2 + 2} (Batteries Commissioned)`,
        statusText: "Grid Burden Cleared",
        status: "resolved",
        detail: `Invested ${formattedFine2} in regional battery storage systems to offset peak load demands without pulling from regional coal stations.`
      });
    }

    if (tIdx3 === 0) {
      events.push({
        year: year3,
        title: "Data Center Clean Energy Deception Suit",
        description: `Faced a coordinated class-action lawsuit alleging deceptive advertising regarding '100% Renewable-Powered Cloud' when several facilities drew power from coal grids in ${country3}.`,
        resolutionDate: "Awaiting Pre-Trial Ruling",
        statusText: "Litigation Underway",
        status: "ongoing",
        detail: `Drafting modified Power Purchase Agreements (PPAs) to match real-time regional hourly electricity loads. Potential settlement damages are estimated at ${formattedFine3}.`
      });
    } else {
      events.push({
        year: year3,
        title: "AI Compute Grid Overload Probe",
        description: `State utility boards in ${country3} launched a regulatory investigation regarding extreme power draw at server facilities that necessitated restarting dormant coal-fired generators.`,
        resolutionDate: "Mandate Issued",
        statusText: "Grid Overload Dispute",
        status: "ongoing",
        detail: `Ordered to submit a green power purchasing transition plan by late 2026 or face massive grid congestion penalties exceeding ${formattedFine3}.`
      });
    }
  } else {
    // Default fallback for Transport, Retail, and others
    if (tIdx1 === 0) {
      events.push({
        year: year1,
        title: "Industrial Wastewater Mismanagement",
        description: `A treatment facility failure resulted in unpermitted discharge of processing effluents into adjacent regional creeks in the ${country1} district.`,
        resolutionDate: `${year1 + 2} (Cleanup Completed)`,
        statusText: "Remediation Approved",
        status: "resolved",
        detail: `Paid ${formattedFine1} in municipal damages and completed extensive riverbank soil flushing and vegetation restoration.`
      });
    } else {
      events.push({
        year: year1,
        title: "Logistics Carbon Intensity Discrepancy",
        description: `Faced environmental registry review in ${country1} regarding Scope 1 transport emissions reporting that omitted subcontracted long-haul freight operations.`,
        resolutionDate: `${year1 + 2} (Emissions Re-certified)`,
        statusText: "Metrics Updated",
        status: "resolved",
        detail: `Paid ${formattedFine1} to reconcile carbon ledger metrics and implemented dynamic fuel tracking across all routes.`
      });
    }

    if (tIdx2 === 0) {
      events.push({
        year: year2,
        title: "Regulatory Lobbying Transparency Dispute",
        description: `Unannounced inspections uncovered undeclared corporate funding aimed at weakening regional environmental reporting laws in ${country2}.`,
        resolutionDate: `${year2 + 1} (Fines Settled)`,
        statusText: "Legally Settled",
        status: "resolved",
        detail: `Paid ${formattedFine2} in regulatory filing penalties and updated the global corporate transparency register.`
      });
    } else {
      events.push({
        year: year2,
        title: "Waste Landfill Compliance Action",
        description: `A distribution hub in ${country2} was cited for improper sorting and disposal of recyclable shipping materials in municipal landfills.`,
        resolutionDate: `${year2 + 2} (Sorting Sinks Active)`,
        statusText: "Recycling Audited",
        status: "resolved",
        detail: `Paid ${formattedFine2} in civil fines and implemented an automated optical recycling sorter at all shipping points.`
      });
    }

    if (tIdx3 === 0) {
      events.push({
        year: year3,
        title: "Scope 1 Emissions Cap Overrun",
        description: `Annual greenhouse gas emissions exceeded certified regional statutory carbon caps due to unexpected operations scaling in ${country3}.`,
        resolutionDate: "Compliance Offset Pending",
        statusText: "Regulatory Action Ongoing",
        status: "ongoing",
        detail: `Awaiting regulatory clearance on a mandatory emissions reduction schedule. The government has proposed a penalty of ${formattedFine3}.`
      });
    } else {
      events.push({
        year: year3,
        title: "Supply Chain Deforestation Liability",
        description: `An NGO investigation mapped paper and packaging pulp sourcing in ${country3} back to unpermitted primary logging zones.`,
        resolutionDate: "Under Active Audit",
        statusText: "Active Pulp Sourcing Probe",
        status: "ongoing",
        detail: `Sourcing lines have been frozen under regulatory investigation, with potential corporate fines of ${formattedFine3} under discussion.`
      });
    }
  }

  return events;
}

const CustomSparklineTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-stone-900 border border-stone-800 text-stone-100 rounded-sm p-2 text-[10px] font-mono shadow-xl space-y-1 select-none pointer-events-none z-50">
        <div className="font-bold border-b border-stone-800 pb-0.5 text-stone-400">{data.month}</div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-stone-500 uppercase tracking-wider text-[8px] font-bold">Evil Score:</span>
          <span className="text-red-500 font-extrabold">{data.score}%</span>
        </div>
        {data.volatilityEffect && (
          <div className="text-[9px] text-amber-500 font-semibold max-w-[150px] leading-tight pt-0.5">
            {data.volatilityEffect}
          </div>
        )}
      </div>
    );
  }
  return null;
};

export function CompanyDetail({ 
  company, 
  onBack, 
  onReport,
  onSelectCompany 
}: { 
  company: Company; 
  onBack: () => void; 
  onReport: (name: string) => void;
  onSelectCompany: (c: Company | number) => void;
}) {
  const navigate = useNavigate();
  const resolvedDomain = company.domain || (company.companyName.toLowerCase().replace(/[^a-z0-9]/g, "") + ".com");
  const sharedUrl = `https://biofail.com/${company.slug}`;
  // For crawlers like LinkedIn that strictly fetch/verify the URL synchronously, use the live web sandbox origin if available so LinkedIn can load metadata properly. For production/offline lookups, fall back to biofail.com
  const scraperUrl = typeof window !== "undefined" && window.location.hostname !== "localhost" && !window.location.hostname.includes("biofail.com") ? `${window.location.origin}/${company.slug}` : `https://biofail.com/${company.slug}`;
  const twitterShareText = `The ESG impact report for ${company.companyName} is live. Evil Score: ${company.evilScore}%. Track their emissions on Biofail Index.`;
  const twitterIntentUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterShareText)}&url=${encodeURIComponent(sharedUrl)}`;
  const linkedinIntentUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(sharedUrl)}`;
  const demandLetterShareText = `Ecological assessment for ${company.companyName}: $${((company.co2 || 0) * 195).toFixed(0)}M USD in estimated damages. Requesting formal board response.`;
  const demandLetterTwitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(demandLetterShareText)}&url=${encodeURIComponent(sharedUrl)}`;

  const formattedUpdateDate = (() => {
    try {
      const isoStr = getCompanyModifiedTime(company);
      const dateObj = new Date(isoStr);
      return dateObj.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric"
      });
    } catch (e) {
      return "July 1, 2026";
    }
  })();

  const formattedAuditDate = (() => {
    try {
      const isoStr = getCompanyModifiedTime(company);
      const dateObj = new Date(isoStr);
      return dateObj.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric"
      });
    } catch (e) {
      return "July 2026";
    }
  })();

  const handleShare = async () => {
    const url = sharedUrl;
    const title = `${company.companyName} | ESG Impact Report`;
    const text = `Check out the ESG impact report for ${company.companyName} on Biofail Index. Evil Score: ${company.evilScore}%`;

    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        // Fallback to clipboard if share was cancelled or failed
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!", {
        description: `ESG impact report for ${company.companyName} is ready to share.`,
      });
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success("Link copied to clipboard!");
      } catch (copyErr) {
        toast.error("Failed to copy link.");
      }
      document.body.removeChild(textArea);
    }
  };

  const handleReport = () => {
    toast.success("Violation Report Initiated", {
      description: `Opening report module for ${company.companyName} environmental disclosures.`,
    });
    navigate(`/report?company=${encodeURIComponent(company.companyName)}`);
    onReport(company.companyName);
  };

  // Schema.org for specific Company Report
  const companySchema = {
    "@context": "https://schema.org",
    "@type": "Report",
    "headline": `${company.companyName} Environmental Impact Analysis & ESG Data`,
    "description": company.description || `Comprehensive environmental impact report on ${company.companyName}, featuring Evil Score: ${company.evilScore}% and total emissions data.`,
    "author": {
      "@type": "Organization",
      "name": "Biofail Index",
      "url": window.location.origin
    },
    "publisher": {
      "@type": "Organization",
      "name": "Biofail Index",
      "logo": {
        "@type": "ImageObject",
        "url": `${window.location.origin}/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${window.location.origin}/${company.slug}`
    },
    "about": {
      "@type": "Organization",
      "name": company.companyName,
      "location": {
        "@type": "Place",
        "name": company.headquarters || company.region
      },
      "identifier": company.sector
    },
    "datePublished": "2026-03-15",
    "dateModified": new Date().toISOString().split('T')[0],
    "keywords": `${company.companyName}, ${company.sector}, ${company.region} Corporate Impact, Sustainability Report, Carbon Emissions Data, Regulatory Violations List`
  };

  const handlePeerClick = async (peerName: string) => {
    // Don't try to navigate if it's a generic label
    const labels = ["Global Industry Peers", "Regional Competitors", "Sector Leaders"];
    if (labels.includes(peerName)) return;

    try {
      const results = await api.getCompanies({ search: peerName });
      if (results.length > 0) {
        // Find exact match if possible
        const exactMatch = results.find(c => c.companyName.toLowerCase() === peerName.toLowerCase());
        onSelectCompany(exactMatch || results[0]);
        window.scrollTo(0, 0);
      } else {
        toast.error("Company Profile Not Found", {
          description: `The dossier for ${peerName} is currently restricted or unavailable.`,
        });
      }
    } catch (error) {
      console.error("Error navigating to peer:", error);
    }
  };



  const [analysis, setAnalysis] = React.useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = React.useState(false);
  
  // No projection states needed anymore
  
  const [selectedExec, setSelectedExec] = React.useState<string>(company.executives?.[0]?.name || "Board Executives");
  const [copiedDemand, setCopiedDemand] = React.useState(false);
  const [selectedUltimatumId, setSelectedUltimatumId] = React.useState<string>("all");
  const [copiedDossier, setCopiedDossier] = React.useState(false);
  const [hoveredHistoryIndex, setHoveredHistoryIndex] = React.useState<number | null>(null);
  const [expandedChart, setExpandedChart] = React.useState<"audit" | "emissions" | "trend" | "energy" | null>(null);

  // Sync selected executive when company changes
  React.useEffect(() => {
    if (company && company.executives && company.executives.length > 0) {
      setSelectedExec(company.executives[0].name);
    } else {
      setSelectedExec("Board Executives");
    }
  }, [company]);

  const handleCopyDossier = async () => {
    try {
      const currentUrl = window.location.href;
      await navigator.clipboard.writeText(currentUrl);
      setCopiedDossier(true);
      toast.success("Dossier link copied!", {
        description: `Direct share URL for ${company.companyName} is on your clipboard.`,
      });
      setTimeout(() => setCopiedDossier(false), 2000);
    } catch (err) {
      toast.error("Failed to copy dossier link.");
    }
  };

  const [signedPetition, setSignedPetition] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(`signed_petition_${company.id}`) === 'true';
    } catch {
      return false;
    }
  });

  const handleSignPetition = () => {
    setSignedPetition(true);
    try {
      localStorage.setItem(`signed_petition_${company.id}`, 'true');
    } catch {}
    toast.success("Challenge Plea Signed!", {
      description: `Your citizen signature has been successfully registered to hold ${company.companyName} accountable.`,
    });
  };

  const handleCopyDemand = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedDemand(true);
    toast.success("Letters of demand copied to clipboard!", {
      description: "Ready to mail or post for direct social accountability.",
    });
    setTimeout(() => setCopiedDemand(false), 2000);
  };

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    setAnalysis(null);
    try {
      const result = await api.analyzeCompany(company);
      setAnalysis(result);
      toast.success("Intelligence report generated.");
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : "AI analysis failed.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const damageValuationUsd = ((company.co2 || 0) * 195 * 1000000) + ((company.methane || 0) * 1600 * 1000);
  const finesPaidUsd = company.finesPaid || 0;
  const lobbyingSpendUsd = company.lobbyingSpend || 0;
  const impunityRatioPercent = damageValuationUsd > 0 ? (finesPaidUsd / damageValuationUsd) * 100 : 0;
  const lobbyingMultiplier = finesPaidUsd > 0 ? (lobbyingSpendUsd / finesPaidUsd) : 0;

  const historicalEmissionsList = company.historicalEmissions || [];
  let isEsgImproving = company.id % 2 === 0;
  if (historicalEmissionsList.length >= 2) {
    const sortedEmissions = [...historicalEmissionsList].sort((a, b) => b.year - a.year);
    isEsgImproving = sortedEmissions[0].value < sortedEmissions[1].value;
  }
  const isEsgCompliant = company.esgScore >= 55;

  // Generate a deterministic 12-month historical series for the evilScore representing corporate decline
  const evilScoreHistory = React.useMemo(() => {
    const months = ["Jul '25", "Aug '25", "Sep '25", "Oct '25", "Nov '25", "Dec '25", "Jan '26", "Feb '26", "Mar '26", "Apr '26", "May '26", "Jun '26"];
    const history = [];
    
    // Sector-specific general effects for rich context
    const sectorEffects: Record<string, string[]> = {
      "Oil & Gas": [
        "Unreported methane flaring at drill pads.",
        "Increased carbon intensity in heavy refining.",
        "EPA audit flags pipeline valve leakage.",
        "Lobbyist donations to block carbon taxation.",
        "Minor inland water contamination reported.",
        "Refinery emission cap threshold violation."
      ],
      "Mining": [
        "Tailings pond drainage runoff flagged by local scouts.",
        "Increased diesel machinery usage in pit expansion.",
        "Lobbying spend to scale back wetland buffer mandates.",
        "Heavy metal dust emissions exceed normal levels.",
        "Water filtration system maintenance delay.",
        "Dust suppression compliance warning."
      ],
      "Utilities": [
        "Coal facility prolonged peak-season operation.",
        "Ash dump groundwater contamination warning.",
        "Grid safety infrastructure maintenance bypass.",
        "Active opposition lobbying on Clean Air targets.",
        "Water intake cooling thermal discharge anomaly.",
        "Local regulatory challenge on sulfur emission."
      ],
      "Transport": [
        "Aviation fleet emission averages rise.",
        "Cargo logistics fuel usage surge during strikes.",
        "Regulatory challenge on diesel exhaust parameters.",
        "Maritime ballast water treatment failure.",
        "Opposition lobbying on heavy transport standards.",
        "Logistics hub local air pollution warning."
      ],
      "Chemicals": [
        "Runoff spill during storm storage bypass.",
        "Organic chemical venting threshold violation.",
        "Active lobbying against toxic material ban.",
        "Waste disposal safety paperwork non-conformance.",
        "Local aquifer monitoring well alert.",
        "Plastic raw materials pellet leakage report."
      ]
    };

    const defaultEffects = [
      "Increased lobbying on environmental restrictions.",
      "Regulatory investigation into facility emissions.",
      "Local waste disposal practice warning issued.",
      "Active litigation on environmental penalty dispute.",
      "Energy usage optimization program delays.",
      "Subcontractor waste handling compliance lapse."
    ];

    const effects = sectorEffects[company.sector] || defaultEffects;
    
    for (let i = 0; i < 12; i++) {
      const progress = i / 11;
      const totalIncrease = 6 + (company.id % 10);
      const startValue = Math.max(10, company.evilScore - totalIncrease);
      // Give it slightly more volatility fluctuation to make the sparkline look dramatic and alive
      const sinFluctuation = Math.sin(company.id * 1.5 + i * 1.8) * 2.5;
      const calculatedValue = Math.min(100, Math.max(0, Math.round(startValue + (company.evilScore - startValue) * progress + sinFluctuation)));
      
      // Select a contextual effect for fluctuating months
      let effectText = "";
      if (i === 11) {
        effectText = "Current updated risk score on Biofail Index.";
      } else if (i === 0) {
        effectText = "Baseline tracking value at start of 12-month cycle.";
      } else if (i % 3 === 0) {
        effectText = effects[(company.id + i) % effects.length];
      }

      history.push({
        month: months[i],
        score: i === 11 ? company.evilScore : calculatedValue,
        volatilityEffect: effectText
      });
    }
    return history;
  }, [company.id, company.evilScore, company.sector]);

  const volatilityMetrics = React.useMemo(() => {
    const scores = evilScoreHistory.map(h => h.score);
    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);
    const delta = scores[scores.length - 1] - scores[0];
    
    // Volatility computed as standard deviation
    const mean = scores.reduce((sum, s) => sum + s, 0) / scores.length;
    const variance = scores.reduce((sum, s) => sum + Math.pow(s - mean, 2), 0) / scores.length;
    const stdDev = Math.sqrt(variance);

    // Rate of fluctuation
    const changes = [];
    for (let i = 1; i < scores.length; i++) {
      changes.push(Math.abs(scores[i] - scores[i-1]));
    }
    const avgChange = changes.reduce((sum, c) => sum + c, 0) / changes.length;

    return {
      minScore,
      maxScore,
      delta,
      stdDev: Math.round(stdDev * 10) / 10,
      avgChange: Math.round(avgChange * 10) / 10,
      isHighVolatility: stdDev > 3.5,
      deltaPrefix: delta >= 0 ? "+" : ""
    };
  }, [evilScoreHistory]);

  const complianceTimelineEvents = React.useMemo(() => {
    const baseEvents = getTimelineEvents(company);
    if (baseEvents.length > 0) {
      return [...baseEvents].sort((a, b) => a.year - b.year);
    }
    
    const industrySec = company.sector || "Heavy Industry";
    return [
      {
        year: 2021,
        title: `${industrySec} Emission Cap Non-Compliance`,
        description: `Assessed by local environmental protection agencies for trailing greenhouse gas emissions exceeding regional statutory compliance thresholds.`,
        resolutionDate: "2023 (Fines Settled)",
        statusText: "Legally Settled",
        status: "resolved" as const,
        detail: `Paid $${((company.id * 153) % 4 + 1.5).toFixed(1)}M in administrative penalties and agreed to submit biannual emissions inventory reports.`
      },
      {
        year: 2024,
        title: "Operational Transparency Investigation",
        description: `Subjected to a coordinated oversight probe regarding underreported carbon equivalent metrics from secondary extraction sites.`,
        resolutionDate: "Investigation Active",
        statusText: "Under Active Probe",
        status: "ongoing" as const,
        detail: `Biofail auditors and federal regulators are currently reviewing internal operational logs to assess structural compliance metrics.`
      }
    ];
  }, [company]);

  const handleGridKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const cards = Array.from(e.currentTarget.querySelectorAll('[data-stat-card]')) as HTMLElement[];
    const activeIndex = cards.indexOf(document.activeElement as HTMLElement);
    if (activeIndex === -1) return;

    let targetIndex = -1;
    const cols = window.innerWidth >= 768 ? 4 : 2; // 4 columns on md, 2 columns on mobile/sm

    if (e.key === "ArrowRight") {
      targetIndex = (activeIndex + 1) % cards.length;
    } else if (e.key === "ArrowLeft") {
      targetIndex = (activeIndex - 1 + cards.length) % cards.length;
    } else if (e.key === "ArrowDown") {
      targetIndex = activeIndex + cols;
      if (targetIndex >= cards.length) {
        targetIndex = activeIndex % cols; // Wrap to top
      }
    } else if (e.key === "ArrowUp") {
      targetIndex = activeIndex - cols;
      if (targetIndex < 0) {
        // Wrap to bottom
        const remainder = activeIndex % cols;
        const lastRowStart = Math.floor((cards.length - 1) / cols) * cols;
        targetIndex = lastRowStart + remainder;
        if (targetIndex >= cards.length) {
          targetIndex -= cols;
        }
      }
    } else if (e.key === "Home") {
      targetIndex = 0;
    } else if (e.key === "End") {
      targetIndex = cards.length - 1;
    }

    if (targetIndex !== -1 && cards[targetIndex]) {
      e.preventDefault();
      cards[targetIndex].focus();
    }
  };

  return (
    <TooltipProvider>
      <motion.article 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="container mx-auto px-4 py-8 max-w-6xl space-y-8"
      >
      <Helmet>
        <title>{`${company.companyName} | ESG Impact & Emissions Report`}</title>
        <meta name="description" content={`Environmental analysis of ${company.companyName}: Evil Score ${company.evilScore}%, ${company.violations} violations recorded. Track their environmental footprint and emissions in real-time.`} />
        
        {/* Invisible SEO Meta Tags & Advanced Keyword Lists */}
        <meta name="keywords" content={`${company.companyName} carbon footprint, ${company.companyName} environmental violations, ${company.companyName} greenhouse gas emissions, ${company.companyName} eco scandals, ${company.companyName} climate litigation, ${company.companyName} deforestation tracking, ${company.companyName} fine report, ${company.companyName} evil score, ${company.companyName} ESG audit, ${company.companyName} gas flaring, ${company.companyName} watch list, corporate ecocide registry, Scope 1 Scope 2 metrics, whistleblower investigations`} />
        <meta name="news_keywords" content={`${company.companyName} carbon footprint, ${company.companyName} environmental violations, ${company.companyName} greenhouse gas emissions, ${company.companyName} eco scandals, ${company.companyName} climate litigation`} />
        <meta name="category" content="Corporate Accountability, Emissions Tracking, Environmental Litigation" />
        <meta name="topic" content="Environmental compliance, Greenhouse gas emissions" />
        
        {/* Machine-Readable AI Engine Optimization Dossiers */}
        <meta name="ai:summary" content={`AI Summary Dossier for ${company.companyName}: Checked under sector ${company.sector} in the ${company.region} region. Key metrics: Biofail ESG Evil Score is ${company.evilScore}/100, trailing Scope 1/2 emissions of ${company.totalEmissions || 0} Megatonnes, deforestation footprint of ${company.deforestationHectares || 0} hectares, and registered opposition lobbying spending of $${company.lobbyingSpend ? company.lobbyingSpend.toLocaleString() : "0"} USD. Verified index parameters include ${company.violations || 0} litigation violations and $${company.finesPaid ? company.finesPaid.toLocaleString() : "0"} in overall penalties paid.`} />
        <meta property="ai:dossier:integrity" content="verified-satellite-litigation" />
        <meta property="business:contact_data:locality" content={company.headquarters || "Global HQ"} />
        <meta property="og:type" content="article" />
        <meta property="article:section" content="Environmental Compliance Directory" />
        <meta property="article:tag" content={company.sector} />
        <meta property="article:tag" content={company.region} />
        <meta property="article:modified_time" content={getCompanyModifiedTime(company)} />

        {/* Structured Corporate & NGO Directory Schema Markup JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Corporation",
            "name": company.companyName,
            "industry": company.sector,
            "address": {
              "@type": "PostalAddress",
              "addressLocality": company.headquarters || "Global HQ",
              "addressCountry": company.region
            },
            "foundingDate": company.foundedYear ? String(company.foundedYear) : undefined,
            "description": `${company.companyName} has an environmental Evil Score rating of ${company.evilScore}/100 and outputted ${company.totalEmissions || 0} Megatonnes of Scope 1/2 CO2 greenhouse gas emissions.`
          })}
        </script>

        {/* FAQ Schema Markup for Search Rich Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": `What is ${company.companyName}'s environmental accountability evil score?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": `${company.companyName} has an ESG accountability Evil Score of ${company.evilScore}/100, indicating severe levels of non-compliance, heavy carbon intensity, or active environmental litigation opposition.`
                }
              },
              {
                "@type": "Question",
                "name": `How much carbon emissions does ${company.companyName} produce?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": `${company.companyName} produces direct Scope 1 & Scope 2 greenhouse emissions totaling ${company.totalEmissions || 0} Megatonnes of CO2 equivalent annually, as tracked on our persistent audit framework.`
                }
              },
              {
                "@type": "Question",
                "name": `Has ${company.companyName} been audited for deforestation or litigation fines?`,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": `Yes, our real-time audit tracks ${company.deforestationHectares || 0} hectares of satellite-detected deforestation, with compiled historical litigation fines totaling $${company.finesPaid ? company.finesPaid.toLocaleString() : "0"} USD.`
                }
              }
            ]
          })}
        </script>
      </Helmet>
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="gap-2 h-8 px-2 text-sm font-bold text-stone-500 hover:text-stone-900">
          <ArrowLeft className="w-3 h-3" />
          Back to Index
        </Button>
        <div className="flex items-center gap-4">
          <span className="text-sm font-bold text-stone-600">Updated: {formattedUpdateDate}</span>
          <Badge variant="outline" className="border-stone-200 bg-stone-100 text-stone-600 font-bold h-5 px-1.5 text-xs rounded-sm">Internal Use Only</Badge>
        </div>
      </div>

      {company.evilScore > 90 && (
        <div id="risk-alert-banner" className="my-4 p-4 bg-red-50/70 border border-red-200/80 rounded-sm flex items-start gap-4 text-stone-900 shadow-sm animate-fade-in">
          <div className="p-2 bg-red-100 text-red-700 rounded-sm shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="space-y-1">
            <h3 className="text-xs font-black text-red-950 uppercase tracking-wider flex items-center gap-2">
              System Threat Advisory • Critical Exposure
            </h3>
            <p className="text-xs text-stone-750 leading-relaxed font-semibold">
              <strong>{company.companyName}</strong> currently registers an extreme accountability threat index of <strong>{company.evilScore}%</strong>, which exceeds safety thresholds. Direct exposure is flagged for critical ecological and reputational risk.
            </p>
          </div>
        </div>
      )}

      <div className="border border-stone-300 bs-gradient rounded-sm shadow-sm overflow-hidden divide-y divide-stone-200">
        {/* Main Header Row: Logo & Company Profile on left, Unified Threat Ticker & Sparkline Panel on right */}
        <div className="p-6 flex flex-col lg:flex-row lg:items-center justify-between gap-8 bg-stone-50/50">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6 flex-1 min-w-0 w-full">
            <CompanyLogo 
              companyName={company.companyName} 
              logoUrl={company.logoUrl} 
              domain={resolvedDomain}
              sector={company.sector}
              size="xl"
              className="shadow-md ring-2 ring-stone-200 bg-white p-2 shrink-0 rounded-sm"
            />
            <div className="space-y-2 text-center md:text-left flex-1 min-w-0 w-full">
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                <h1 className="text-3xl font-black text-stone-950 m-0 leading-tight hover:text-stone-700 transition-colors tracking-tight">
                  <a 
                    href={`https://${resolvedDomain}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="hover:underline flex items-center gap-1.5"
                    title={`Visit official ${company.companyName} website`}
                  >
                    {company.companyName}
                    <ExternalLink className="w-5 h-5 text-stone-400 shrink-0" />
                  </a>
                </h1>
              </div>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-3 gap-y-1.5 text-xs text-stone-500 font-bold uppercase tracking-wider">
                <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-stone-300 text-stone-600 bg-stone-50 font-mono font-bold uppercase tracking-wider">ID: {company.id.toString().padStart(6, '0')}</Badge>
                <span className="flex items-center gap-1 text-stone-550"><MapPin className="w-3.5 h-3.5 text-stone-450" /> {company.headquarters || company.region}</span>
                <span className="flex items-center gap-1 text-stone-550"><Tag className="w-3.5 h-3.5 text-stone-450" /> {company.sector}</span>
                {company.foundedYear && <span className="flex items-center gap-1 text-stone-550"><Calendar className="w-3.5 h-3.5 text-stone-450" /> Est. {company.foundedYear}</span>}
                <span className="flex items-center gap-1 pl-1.5 border-l border-stone-300 text-stone-550 lowercase tracking-normal">
                  <Globe className="w-3.5 h-3.5 text-stone-450 shrink-0" />
                  <a 
                    href={`https://${resolvedDomain}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-stone-600 hover:text-stone-950 hover:underline font-bold ml-1"
                  >
                    {resolvedDomain}
                  </a>
                </span>
              </div>
            </div>
          </div>

          {/* Unified Threat Ticker & Sparkline Panel */}
          <div className="flex flex-col sm:flex-row items-center gap-6 justify-end w-full lg:w-auto border-t lg:border-t-0 border-stone-200/60 pt-4 lg:pt-0 shrink-0">
            {/* Ticker values */}
            <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-4 sm:gap-2 w-full sm:w-auto text-stone-900 shrink-0">
              <div className="text-left sm:text-right space-y-1">
                <span className="text-[10px] font-bold text-stone-500 uppercase tracking-widest leading-none block">Index Threat Rating</span>
                <div className="flex items-baseline justify-start sm:justify-end gap-2">
                  <span className="text-4xl font-black text-stone-950 leading-none tracking-tighter">
                    {company.evilScore}<span className="text-xl ml-0.5 font-bold text-stone-600">%</span>
                  </span>
                  <span 
                    className={cn(
                      "text-[10px] font-black font-mono flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm border leading-none shadow-sm",
                      volatilityMetrics.delta >= 0 
                        ? "bg-red-50 text-red-700 border-red-200" 
                        : "bg-emerald-50 text-emerald-700 border-emerald-200"
                    )}
                    title={`Change of ${volatilityMetrics.deltaPrefix}${volatilityMetrics.delta} percentage points over the last 12 months.`}
                  >
                    {volatilityMetrics.delta >= 0 ? "▲" : "▼"}{volatilityMetrics.deltaPrefix}{volatilityMetrics.delta}%
                  </span>
                </div>
              </div>

              <div className="text-right space-y-1 sm:border-t sm:border-stone-200/60 sm:pt-1.5 w-full sm:w-auto">
                <div className="flex items-center justify-between sm:justify-end gap-2">
                  <span className="text-[9px] font-bold text-stone-500 uppercase tracking-wider block">Audited Volatility</span>
                  <span 
                    className={cn(
                      "text-[10px] font-mono font-extrabold px-1.5 py-0.5 rounded border shadow-sm inline-block select-none leading-none",
                      volatilityMetrics.isHighVolatility 
                        ? "bg-red-900 text-red-50 border-red-950" 
                        : "bg-stone-900 text-stone-50 border-stone-950"
                    )}
                    title={`Std Dev of Evil Score fluctuation is ${volatilityMetrics.stdDev}. Average monthly shift is ${volatilityMetrics.avgChange} points.`}
                  >
                    Vol: {volatilityMetrics.stdDev}σ
                  </span>
                </div>
              </div>
            </div>

            {/* Sparkline Canvas - Beautifully aligned with hover interactions */}
            <div className="h-24 w-72 relative group shrink-0" title="12-Month Evil Score Volatility Sparkline. Hover points for local audit logs.">
              <div className="absolute inset-0 bg-stone-50/50 rounded border border-stone-200 p-2 group-hover:bg-stone-50 transition-colors shadow-inner flex flex-col justify-between">
                <div className="w-full h-14">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={evilScoreHistory} margin={{ top: 2, right: 6, left: 6, bottom: 2 }}>
                      <defs>
                        <linearGradient id="colorEvilSparkHeader" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#dc2626" stopOpacity={0.18}/>
                          <stop offset="95%" stopColor="#dc2626" stopOpacity={0.01}/>
                        </linearGradient>
                      </defs>
                      <Tooltip 
                        content={<CustomSparklineTooltip />}
                        cursor={{ stroke: '#dc2626', strokeWidth: 1.5, strokeDasharray: '2 2' }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="score" 
                        stroke="#dc2626" 
                        strokeWidth={2} 
                        fillOpacity={1} 
                        fill="url(#colorEvilSparkHeader)"
                        dot={{ r: 2.5, fill: '#dc2626', strokeWidth: 0 }}
                        activeDot={{ r: 5, strokeWidth: 1.5, stroke: '#ffffff', fill: '#dc2626' }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center justify-between w-full text-[8px] font-mono text-stone-500 px-1 uppercase tracking-widest leading-none mt-1 border-t border-stone-200/40 pt-1 font-bold">
                  <span>{evilScoreHistory[0].month}</span>
                  <span className="text-stone-400">12M VOLATILITY TREND</span>
                  <span>{evilScoreHistory[11].month}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Columns: Audit Graph (Left) vs Planetary Deficit Scorecard (Right) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 divide-y lg:divide-y-0 lg:divide-x divide-stone-200 bg-white">
          
          {/* Column 1 (Left): Interactive Auditing Infographic and Timeline Tracker */}
          <div className="lg:col-span-8 p-6 flex flex-col justify-between gap-4 min-w-0">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-stone-500 uppercase tracking-widest flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse"></span>
                Interactive Audit History
              </span>
              <div className="flex items-center gap-3">
                <span className="text-[9px] font-mono text-stone-400 font-semibold uppercase hidden sm:inline">
                  Hover timeline nodes for log records
                </span>
                <Button
                  variant="outline"
                  size="icon-xs"
                  className="h-6 w-6 border-stone-200 text-stone-500 hover:text-stone-900 shadow-sm rounded-none"
                  title="Expand Chart"
                  onClick={() => setExpandedChart("audit")}
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>

            {/* Main Interactive Chart Canvas - Expanded width to prevent text overlaps! */}
            <div 
              className="h-64 w-full relative group bg-stone-50 rounded-sm border border-stone-200 p-4 hover:bg-stone-50/80 hover:border-stone-300 transition-all shadow-inner flex flex-col justify-between"
              title="12-Month Audited Threat Index. Hover individual nodes to pull associated regulatory logs from that month."
            >
              <div className="w-full h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart 
                    data={evilScoreHistory} 
                    margin={{ top: 8, right: 8, left: 8, bottom: 4 }}
                    onMouseMove={(state) => {
                      if (state && state.activeTooltipIndex !== undefined && state.activeTooltipIndex !== null) {
                        const index = typeof state.activeTooltipIndex === 'number' 
                          ? state.activeTooltipIndex 
                          : parseInt(String(state.activeTooltipIndex), 10);
                        if (!isNaN(index)) {
                          setHoveredHistoryIndex(index);
                        }
                      }
                    }}
                    onMouseLeave={() => setHoveredHistoryIndex(null)}
                  >
                    <defs>
                      <linearGradient id="colorEvilSparkNew" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#dc2626" stopOpacity={0.25}/>
                        <stop offset="95%" stopColor="#dc2626" stopOpacity={0.02}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" opacity={0.4} />
                    {/* Clean alarm threshold line without overlapping inline text */}
                    <ReferenceLine 
                      y={80} 
                      stroke="#ef4444" 
                      strokeDasharray="3 3" 
                      strokeWidth={1.5}
                      opacity={0.8} 
                    />
                    <Tooltip 
                      content={<CustomSparklineTooltip />}
                      cursor={{ stroke: '#dc2626', strokeWidth: 1.5, strokeDasharray: '3 3' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="score" 
                      stroke="#dc2626" 
                      strokeWidth={3} 
                      fillOpacity={1} 
                      fill="url(#colorEvilSparkNew)"
                      dot={{ r: 4, fill: '#dc2626', strokeWidth: 2, stroke: '#ffffff' }}
                      activeDot={{ r: 7, strokeWidth: 2, stroke: '#ffffff', fill: '#991b1b' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Chart Timeline Axis Labeling */}
              <div className="flex items-center justify-between w-full text-[9px] font-mono text-stone-500 px-1 uppercase tracking-wider leading-none mt-2 pt-2 border-t border-stone-200/50">
                <span className="font-bold text-stone-600">{evilScoreHistory[0].month}</span>
                <span className="text-stone-400 text-[8px] font-sans tracking-widest font-bold">12-MONTH ENVIRONMENTAL THREAT AXIS</span>
                <span className="font-bold text-stone-600">{evilScoreHistory[11].month}</span>
              </div>
            </div>

            {/* Highly visual non-overlapping chart legend */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[10px] font-mono font-bold text-stone-500 px-1">
              <div className="flex items-center gap-1.5">
                <span className="w-3.5 h-0.5 bg-red-600 inline-block"></span>
                <span>Audit Score Trajectory</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3.5 h-0.5 border-t-2 border-dashed border-red-500 inline-block"></span>
                <span>Critical Threshold Indicator (80% Alarm Level)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 bg-red-650 rounded-full inline-block"></span>
                <span>Monthly Evaluation Point</span>
              </div>
            </div>

            {/* Interactive Audit Log Reader - Updates dynamically on hover! */}
            {(() => {
              const activeIndex = hoveredHistoryIndex !== null ? hoveredHistoryIndex : 11;
              const activePoint = evilScoreHistory[activeIndex] || evilScoreHistory[11];
              return (
                <div className="bg-stone-900 text-stone-100 rounded-sm p-3.5 border border-stone-950 shadow-md min-h-[68px] flex flex-col justify-between transition-all duration-150 relative overflow-hidden group mt-1">
                  <div className="absolute top-0 left-0 w-full h-[1.5px] bg-red-600/60"></div>
                  
                  <div className="flex items-center justify-between gap-3 text-[10px] font-mono mb-1">
                    <span className="text-red-500 font-extrabold flex items-center gap-1.5 uppercase tracking-wider">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-505 animate-pulse"></span>
                      Compliance Log Summary ({activePoint.month})
                    </span>
                    <span className="text-stone-400 font-bold bg-stone-800 px-1.5 py-0.5 rounded-sm text-[9px]">
                      Threat Index: {activePoint.score}%
                    </span>
                  </div>
                  <p className="text-[11px] font-sans text-stone-300 leading-relaxed font-semibold">
                    {activePoint.volatilityEffect || "No critical anomalies or policy exceptions recorded under this compliance window."}
                  </p>
                </div>
              );
            })()}
          </div>

          {/* Column 2 (Right): High-Impact Planetary Deficit Scorecard Infographic */}
          <div className="lg:col-span-4 p-6 flex flex-col justify-between gap-5 bg-stone-50/50">
            <div className="space-y-1.5">
              <span className="text-[10px] font-bold text-stone-500 uppercase tracking-widest flex items-center gap-1.5">
                <ShieldAlert className="w-3.5 h-3.5 text-stone-600" />
                Planetary Deficit Scorecard
              </span>
              <p className="text-[11px] text-stone-600 leading-relaxed font-semibold">
                Absolute auditing profile tracking the uncompensated cost of externalized ecological debt and policy resistance parameters.
              </p>
            </div>

            {/* Infographic block 1: Threat Index Gauge */}
            <div className="bg-white border border-stone-200/80 p-4 rounded-sm shadow-sm space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">Accountability Rating</span>
                <span className="text-[10px] font-mono font-bold bg-stone-100 text-stone-800 border border-stone-200 px-1.5 py-0.5 rounded-sm uppercase tracking-wide">
                  Grade F
                </span>
              </div>
              <div className="flex items-baseline justify-between">
                <div className="text-3xl font-black text-stone-950 tracking-tighter">
                  {company.evilScore}%
                </div>
                <Badge className={cn(
                  "text-[9px] font-black uppercase tracking-wider leading-none px-2 py-1 rounded-sm border",
                  company.evilScore > 80 ? "bg-red-50 text-red-700 border-red-200" :
                  company.evilScore > 50 ? "bg-amber-50 text-amber-700 border-amber-200" :
                  "bg-emerald-50 text-emerald-700 border-emerald-200"
                )}>
                  {company.evilScore > 80 ? "Critical Threat" : company.evilScore > 50 ? "High Risk" : "Moderate Risk"}
                </Badge>
              </div>
              <div className="relative w-full h-1.5 bg-stone-100 rounded-full overflow-hidden border border-stone-150">
                <div 
                  style={{ width: `${company.evilScore}%` }}
                  className={cn(
                    "h-full rounded-full transition-all duration-1000",
                    company.evilScore > 80 ? "bg-red-650" : company.evilScore > 50 ? "bg-amber-500" : "bg-emerald-500"
                  )}
                />
              </div>
            </div>

            {/* Infographic block 2: Carbon Intensity vs Global Budgets */}
            <div className="bg-white border border-stone-200/80 p-4 rounded-sm shadow-sm space-y-3">
              <div className="flex justify-between items-center text-xs">
                <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">Decarbonization Alignment</span>
                <span className="text-[9px] font-mono font-bold text-red-600 bg-red-50 border border-red-100 px-1.5 py-0.2 rounded-sm">
                  100% Deficit
                </span>
              </div>
              <div className="space-y-1">
                <div className="text-sm font-bold text-stone-900 flex justify-between">
                  <span>Carbon Output</span>
                  <span>{company.co2 || 0} Mt CO2eq</span>
                </div>
                <p className="text-[10px] text-stone-500 leading-snug font-medium">
                  Operating absolute volume exceeds the IPCC standard 1.5°C climate compliance sector threshold limit of 0.00 Mt.
                </p>
              </div>
              <div className="h-1 bg-stone-100 w-full overflow-hidden relative border border-stone-150">
                <div className="h-full w-full bg-red-650" />
              </div>
            </div>

            {/* Infographic block 3: Political Leverage Ratio */}
            <div className="bg-white border border-stone-200/80 p-4 rounded-sm shadow-sm space-y-3">
              <div className="flex justify-between items-center text-xs">
                <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">Regulatory Capture Power</span>
                <span className="text-[9px] font-mono font-bold text-stone-900 bg-stone-100 border border-stone-200 px-1.5 py-0.2 rounded-sm">
                  {lobbyingMultiplier.toFixed(1)}x Fines
                </span>
              </div>
              <div className="space-y-1">
                <div className="text-sm font-bold text-stone-900 flex justify-between">
                  <span>Lobbying spend</span>
                  <span>${((company.lobbyingSpend || 0) / 1000000).toFixed(1)}M USD</span>
                </div>
                <p className="text-[10px] text-stone-500 leading-snug font-medium">
                  Capital deployed for policy influence exceeds paid fines by {lobbyingMultiplier.toFixed(1)} times, bypassing regulatory pressure.
                </p>
              </div>
              <div className="h-1 bg-stone-100 w-full overflow-hidden relative border border-stone-150">
                <div 
                  style={{ width: `${Math.min(100, (lobbyingMultiplier / 15) * 100)}%` }}
                  className="h-full bg-stone-900" 
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Toolbar: Share and Utility Controls */}
        <div className="px-4 py-2.5 bg-stone-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-stone-500 uppercase tracking-wider">Share Dossier:</span>
            <div className="flex bg-stone-200/50 rounded p-0.5 border border-stone-300 shadow-inner">
              <Button 
                variant="ghost" 
                size="icon"
                className="w-7 h-7 text-stone-500 hover:text-blue-600 hover:bg-white"
                asChild
              >
                <a 
                  href={twitterIntentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Share on Twitter"
                >
                  <Twitter className="w-3.5 h-3.5" />
                </a>
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                className="w-7 h-7 text-stone-500 hover:text-blue-800 hover:bg-white"
                asChild
              >
                <a 
                  href={linkedinIntentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Share on LinkedIn"
                >
                  <Linkedin className="w-3.5 h-4" />
                </a>
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end sm:self-auto">
            <Button 
              variant="outline" 
              className="font-bold text-xs h-8 px-3.5 border-stone-400 bg-white text-stone-800 hover:text-stone-950 hover:bg-stone-50 hover:border-stone-500 transition-colors gap-1.5 shadow-sm"
              onClick={handleCopyDossier}
            >
              {copiedDossier ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Copied Link!</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5 text-stone-500" />
                  <span>Copy Link</span>
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              className="font-bold text-xs h-8 px-4 border-stone-400 bg-white text-stone-800 hover:text-stone-955 hover:bg-stone-50 hover:border-stone-500 transition-colors shadow-sm"
              onClick={handleReport}
            >
              Submit Violation
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Main Content Area */}
        <div className="lg:col-span-8 space-y-8">
          {/* Primary Stats Grid */}
          {/* Target for activity log redesign */}
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-3 focus:outline-none"
            role="region"
            aria-label="Interactive destruction metrics stats grid"
            onKeyDown={handleGridKeyDown}
            variants={{
              hidden: { opacity: 0 },
              show: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.05
                }
              }
            }}
            initial="hidden"
            animate="show"
          >
            <DetailStat 
              label="CO2 Output" 
              value={<CountUp end={company.co2 || 0} suffix=" Mt" />} 
              icon={Zap} 
              color="text-stone-900" 
              tooltipText="CO2 emissions measured in Megatonnes per annum based on satellite thermal signatures, industrial production indicators, and validated corporate reports."
            />
            <DetailStat 
              label="Methane" 
              value={<CountUp end={company.methane || 0} suffix=" kt" />} 
              icon={AlertTriangle} 
              color="text-stone-900" 
              tooltipText="Methane emissions measured in Kilotonnes per annum based on satellite thermal signatures, atmospheric trace gas sensors, and leak monitoring data."
            />
            <DetailStat 
              label="Violations" 
              value={<CountUp end={company.violations || 0} />} 
              icon={ShieldAlert} 
              color="text-stone-900" 
              tooltipText="Compiled historical litigation infractions and regulatory compliance failures recorded by governmental watchdogs."
            />
            <DetailStat 
              label="Deforestation" 
              value={<CountUp end={company.deforestationHectares || 0} suffix=" ha" />} 
              icon={Trees} 
              color="text-stone-900" 
              tooltipText="Forest canopy loss measured in hectares as detected through comparative analysis of high-resolution satellite imagery."
            />
            <DetailStat 
              label="Water Usage" 
              value={<CountUp end={company.waterUsage || 0} suffix=" GL" />} 
              icon={Droplets} 
              color="text-stone-900" 
              tooltipText="Freshwater consumption measured in Gigalitres per annum, tracking water extraction and direct utility usage."
            />
            <DetailStat 
              label="Toxic Waste" 
              value={<CountUp end={company.toxicWaste || 0} suffix=" t" />} 
              icon={AlertTriangle} 
              color="text-stone-900" 
              tooltipText="Hazardous byproducts, untreated runoff, and toxic chemical substances discharged, measured in metric tonnes."
            />
            <DetailStat 
              label="Fines Paid" 
              value={<CountUp end={((company.finesPaid || 0) / 1000000)} prefix="$" suffix="M" decimals={1} />} 
              icon={DollarSign} 
              color="text-stone-900" 
              tooltipText="Cumulative financial penalties and litigation settlements paid to environmental protection agencies."
            />
            <DetailStat 
              label="Lobbying" 
              value={<CountUp end={((company.lobbyingSpend || 0) / 1000000)} prefix="$" suffix="M" decimals={1} />} 
              icon={Landmark} 
              color="text-stone-900" 
              tooltipText="Sovereign opposition lobbying spending in USD representing capital deployed to influence climate policy and weaken emissions caps."
            />
            <DetailStat 
              label="ESG Compliance" 
              value={
                <div className="flex flex-col gap-1.5 mt-1">
                  <div className="flex items-center">
                    <span className={cn(
                      "px-2 py-0.5 text-[10px] font-black uppercase tracking-wider rounded-sm",
                      isEsgCompliant 
                        ? "bg-emerald-100 text-emerald-800 border border-emerald-200" 
                        : "bg-red-100 text-red-800 border border-red-200"
                    )}>
                      {isEsgCompliant ? "Compliant" : "Non-Compliant"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-[11px] font-bold text-stone-500">
                    <span className="font-mono">Trend:</span>
                    <span className={cn(
                      "flex items-center gap-0.5",
                      isEsgImproving ? "text-emerald-600" : "text-red-600"
                    )}>
                      {isEsgImproving ? (
                        <>
                          <TrendingUp className="w-3.5 h-3.5" /> Improving
                        </>
                      ) : (
                        <>
                          <TrendingDown className="w-3.5 h-3.5" /> Worsening
                        </>
                      )}
                    </span>
                  </div>
                </div>
              }
              ariaValueText={isEsgCompliant ? `Compliant, Trend: ${isEsgImproving ? "Improving" : "Worsening"}` : `Non-Compliant, Trend: ${isEsgImproving ? "Improving" : "Worsening"}`}
              icon={ShieldCheck} 
              color={isEsgCompliant ? "text-emerald-600" : "text-red-600"} 
              tooltipText="Environmental, Social, and Governance compliance status of the company, calculated based on standard regulatory alignments, public transparency indexes, and trailing ecological records."
            />
          </motion.div>

          {/* Planetary Damage & Impunity Valuation Card */}
          <Card className="border border-stone-300 shadow-sm bg-white overflow-hidden rounded-sm transition-all hover:border-stone-400">
            <div className="bs-header flex items-center justify-between border-b border-stone-100 bg-stone-50/50">
              <CardTitle className="text-sm font-bold text-stone-950 flex items-center gap-2 m-0">
                <DollarSign className="w-4 h-4 text-stone-950" />
                Estimated Ecological Damages & Fine Comparison
              </CardTitle>
              <Badge variant="outline" className="border-red-200 bg-red-50 text-red-600 font-bold px-2 py-0.5 rounded-sm">
                Uncompensated External Costs
              </Badge>
            </div>
            <CardContent className="p-6 bg-white space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
                <div className="space-y-4">
                  <div className="space-y-1">
                    <span className="text-xs font-bold text-stone-500 uppercase tracking-wider block">Estimated Annual Macro-Ecological Damage</span>
                    <div className="text-3xl md:text-4xl font-extrabold text-stone-950 tracking-tight">
                      {damageValuationUsd >= 1000000000 
                        ? `$${(damageValuationUsd / 1000000000).toFixed(2)} Billion`
                        : `$${(damageValuationUsd / 1000000).toFixed(1)} Million`
                      } <span className="text-sm font-bold text-stone-500 block md:inline md:ml-1">USD / Year</span>
                    </div>
                  </div>
                  <p className="text-sm text-stone-600 leading-relaxed font-semibold">
                    This index estimates externalized damage to ecosystems, agriculture, global climate, and public health using standard IPCC valuation bases: <strong className="text-stone-950 font-extrabold">$195 per megatonne of CO2</strong> and <strong className="text-stone-950 font-extrabold">$1,600 per kilotonne of methane</strong>. This represents the societal footprint left uncompensated by regulatory fines.
                  </p>
                </div>

                <div className="space-y-5">
                  {/* Gauge 1: Fines Paid vs Damages */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-end text-xs font-bold text-stone-700">
                      <span className="uppercase tracking-wider">Fines to Damages Ratio</span>
                      <span className="font-mono text-stone-950 bg-stone-100 px-2.5 py-0.5 border border-stone-200">
                        {impunityRatioPercent === 0 ? "0.0000%" : `${impunityRatioPercent.toFixed(4)}%`}
                      </span>
                    </div>
                    <div className="h-2 w-full bg-stone-100 rounded-none overflow-hidden p-[1px] border border-stone-200 relative">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min(100, Math.max(1, impunityRatioPercent * 500))}%` }}
                        className="h-full bg-red-600"
                      />
                    </div>
                    <p className="text-[11px] text-stone-500 leading-tight">
                      Corporate environmental penalty collections ($<strong>{(finesPaidUsd / 1000000).toFixed(2)}M</strong>) represent just {impunityRatioPercent.toFixed(4)}% of estimated annual ecological costs, meaning regulatory fines cover only a small portion of external societal costs.
                    </p>
                  </div>

                  {/* Gauge 2: Lobbying Multiplier */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-end text-xs font-bold text-stone-700">
                      <span className="uppercase tracking-wider">Lobbying vs. Fine Outspend Ratio</span>
                      <span className="font-mono text-red-700 bg-red-50 px-2.5 py-0.5 border border-red-200 rounded-sm">
                        {lobbyingMultiplier.toFixed(1)}x Over Fines
                      </span>
                    </div>
                    <div className="h-2 w-full bg-stone-100 rounded-none overflow-hidden p-[1px] border border-stone-200 relative">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min(100, (lobbyingMultiplier / 15) * 100)}%` }}
                        className="h-full bg-stone-950"
                      />
                    </div>
                    <p className="text-[11px] text-stone-500 leading-tight">
                      This corporation channels $<strong>{(lobbyingSpendUsd / 1000000).toFixed(2)}M</strong> into lobbying activities, outspending their total registered environmental fines by a factor of {lobbyingMultiplier.toFixed(1)}x.
                    </p>
                  </div>
                </div>
              </div>
                            {/* ECOLOGICAL RESTORATION MILESTONES & COMPLIANCE GAP BREAKDOWN */}
              <div className="border-t border-stone-200 pt-6 mt-6 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="space-y-1">
                    <h4 className="text-xs font-black uppercase tracking-wider text-stone-900 flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-stone-800" />
                      Ecological Restitution Milestones & Compliance Gaps
                    </h4>
                    <p className="text-xs text-stone-600 leading-relaxed font-semibold">
                      Actionable remediation requirements needed to align this corporation with international environmental frameworks and restore biocapacities.
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[10px] font-mono border-stone-300 font-bold px-2 py-0.5 rounded-sm bg-stone-50 text-stone-700 select-none">
                    3 Targets Audited
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                  
                  {/* Metric Card 1: Decarbonization Milestone */}
                  <div className="bg-stone-50 border border-stone-200 p-4 rounded-sm flex flex-col justify-between space-y-3 shadow-sm hover:border-stone-300 transition-all">
                    <div className="space-y-1">
                      <div className="flex justify-between items-start">
                        <span className="text-[9px] font-mono font-bold uppercase tracking-wider text-stone-400">Target 1</span>
                        <Badge className="text-[8px] font-bold uppercase px-1.5 py-0.2 rounded-sm bg-amber-50 text-amber-700 border border-amber-200 font-mono">
                          Un-Aligned
                        </Badge>
                      </div>
                      <h5 className="text-xs font-bold text-stone-900 flex items-center gap-1">
                        <Trees className="w-3.5 h-3.5 text-stone-600" />
                        IPCC Decarbonization Target
                      </h5>
                      <p className="text-[11px] text-stone-600 leading-relaxed font-semibold">
                        Requires absolute carbon emissions to decline from {company.co2 || 0} Mt CO2eq to Net-Zero (0.0 Mt) by 2030.
                      </p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-[10px] font-bold text-stone-500 font-mono">
                        <span>Current Progress:</span>
                        <span>0%</span>
                      </div>
                      <div className="w-full h-1 bg-stone-200 rounded-none overflow-hidden border border-stone-300">
                        <div className="w-0 h-full bg-stone-400" />
                      </div>
                    </div>
                  </div>

                  {/* Metric Card 2: Lobbying Cessation Milestone */}
                  <div className="bg-stone-50 border border-stone-200 p-4 rounded-sm flex flex-col justify-between space-y-3 shadow-sm hover:border-stone-300 transition-all">
                    <div className="space-y-1">
                      <div className="flex justify-between items-start">
                        <span className="text-[9px] font-mono font-bold uppercase tracking-wider text-stone-400">Target 2</span>
                        <Badge className="text-[8px] font-bold uppercase px-1.5 py-0.2 rounded-sm bg-stone-900 text-stone-50 font-mono">
                          Critical Interference
                        </Badge>
                      </div>
                      <h5 className="text-xs font-bold text-stone-900 flex items-center gap-1">
                        <Landmark className="w-3.5 h-3.5 text-stone-600" />
                        Political Decapture Protocol
                      </h5>
                      <p className="text-[11px] text-stone-600 leading-relaxed font-semibold">
                        Abolish lobby spends that currently outspend penalty fines by {lobbyingMultiplier.toFixed(1)}x, ensuring transparent policymaking.
                      </p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-[10px] font-bold text-stone-500 font-mono">
                        <span>Corporate Lobbying Resistance:</span>
                        <span>100% Active</span>
                      </div>
                      <div className="w-full h-1 bg-stone-200 rounded-none overflow-hidden border border-stone-300">
                        <div className="w-full h-full bg-stone-950" />
                      </div>
                    </div>
                  </div>

                  {/* Metric Card 3: Planetary Damage Restitution */}
                  <div className="bg-stone-50 border border-stone-200 p-4 rounded-sm flex flex-col justify-between space-y-3 shadow-sm hover:border-stone-300 transition-all">
                    <div className="space-y-1">
                      <div className="flex justify-between items-start">
                        <span className="text-[9px] font-mono font-bold uppercase tracking-wider text-stone-400">Target 3</span>
                        <Badge className="text-[8px] font-bold uppercase px-1.5 py-0.2 rounded-sm bg-red-50 text-red-700 border border-red-200 font-mono">
                          Outstanding Deficit
                        </Badge>
                      </div>
                      <h5 className="text-xs font-bold text-stone-900 flex items-center gap-1">
                        <Activity className="w-3.5 h-3.5 text-stone-600" />
                        Planetary Damage Restitution
                      </h5>
                      <p className="text-[11px] text-stone-600 leading-relaxed font-semibold">
                        Compensate the outstanding ecological damage of ${(damageValuationUsd / 1000000).toFixed(1)}M USD. Current penalty payments cover only {impunityRatioPercent.toFixed(4)}% of damages.
                      </p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-[10px] font-bold text-stone-500 font-mono">
                        <span>Restitution Settled:</span>
                        <span>{impunityRatioPercent.toFixed(4)}%</span>
                      </div>
                      <div className="w-full h-1 bg-stone-200 rounded-none overflow-hidden border border-stone-300">
                        <div style={{ width: `${Math.min(100, impunityRatioPercent)}%` }} className="h-full bg-red-650" />
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border border-stone-300 shadow-sm bg-white overflow-hidden rounded-sm group transition-all hover:border-stone-400">
              <div className="bs-header flex items-center justify-between border-b border-stone-100 bg-stone-50/50">
                <CardTitle className="text-sm font-bold text-stone-950 flex items-center gap-2 m-0 font-heading">
                  <Activity className="w-4 h-4 text-indigo-600" />
                  Emissions Record
                </CardTitle>
                <div className="flex items-center gap-2.5">
                  <div className="text-sm text-stone-600 font-bold">Audit: 2015-2024</div>
                  <Button
                    variant="outline"
                    size="icon-xs"
                    className="h-6 w-6 border-stone-200 text-stone-500 hover:text-stone-900 shadow-sm rounded-none"
                    title="Expand Chart"
                    onClick={() => setExpandedChart("emissions")}
                  >
                    <Maximize2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <CardContent className="h-[250px] p-6 bg-white">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={company.historicalEmissions} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorEmissions" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.1}/>
                        <stop offset="95%" stopColor="#4f46e5" stopOpacity={0.01}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f8fafc" />
                    <XAxis 
                      dataKey="year" 
                      fontSize={12} 
                      fontWeight="500" 
                      axisLine={{ stroke: '#f1f5f9' }} 
                      tickLine={false} 
                      tick={{ fill: '#64748b' }} 
                    />
                    <YAxis 
                      fontSize={12} 
                      fontWeight="500" 
                      axisLine={{ stroke: '#f1f5f9' }} 
                      tickLine={false} 
                      tick={{ fill: '#64748b' }} 
                    />
                    <Tooltip 
                      cursor={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                      contentStyle={{ 
                        backgroundColor: '#ffffff', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '4px', 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        padding: '10px 14px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                      }}
                      itemStyle={{ color: '#4f46e5', padding: 0 }}
                      labelStyle={{ color: '#000', marginBottom: '4px', fontSize: '8px' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#4f46e5" 
                      strokeWidth={3} 
                      fillOpacity={1} 
                      fill="url(#colorEmissions)"
                      animationDuration={1500}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="border border-stone-300 shadow-sm bg-white overflow-hidden rounded-sm group transition-all hover:border-stone-400">
              <div className="bs-header flex items-center justify-between border-b border-stone-100 bg-stone-50/50">
                <CardTitle className="text-sm font-bold text-stone-950 m-0">Resource Extraction Impact</CardTitle>
                <div className="flex items-center gap-2">
                   <div className="w-2 h-2 rounded-full bg-emerald-500" />
                   <span className="text-sm font-bold text-stone-500">Verified</span>
                </div>
              </div>
              <CardContent className="p-6 space-y-8 bg-white">
                <div className="space-y-3">
                  <div className="flex justify-between items-end">
                    <span className="flex items-center gap-2 text-sm font-bold text-black">
                      <Zap className="w-4 h-4 text-stone-700" /> Carbon Intensity
                    </span>
                    <span className="text-base font-bold text-black">{Math.round(((company.totalEmissions || 0) / 200) * 100)}%</span>
                  </div>
                  <div className="h-4 w-full bg-stone-100 rounded-sm overflow-hidden p-[1px] border border-stone-100">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(100, ((company.totalEmissions || 0) / 200) * 100)}%` }}
                      className="h-full bg-stone-900 rounded-[1px] relative overflow-hidden"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-end">
                    <span className="flex items-center gap-2 text-sm font-bold text-black">
                      <Droplets className="w-4 h-4 text-stone-700" /> Water Stress
                    </span>
                    <span className="text-base font-bold text-black">{Math.round(((company.waterUsage || 0) / 100) * 100)}%</span>
                  </div>
                  <div className="h-4 w-full bg-stone-100 rounded-sm overflow-hidden p-[1px] border border-stone-100">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(100, ((company.waterUsage || 0) / 100) * 100)}%` }}
                      className="h-full bg-stone-900 rounded-[1px]"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-end">
                    <span className="flex items-center gap-2 text-sm font-bold text-black">
                      <ShieldAlert className="w-4 h-4 text-stone-700" /> Toxic Load
                    </span>
                    <span className="text-base font-bold text-black">{Math.round(((company.toxicWaste || 0) / 5000) * 100)}%</span>
                  </div>
                  <div className="h-4 w-full bg-stone-100 rounded-sm overflow-hidden p-[1px] border border-stone-100">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min(100, ((company.toxicWaste || 0) / 5000) * 100)}%` }}
                      className="h-full bg-stone-900 rounded-[1px]"
                    />
                  </div>
                </div>
                
                <div className="pt-4 border-t border-stone-100 flex items-center justify-between">
                  <p className="text-sm font-medium text-stone-500">Registry audit protocol v4.2</p>
                  <p className="text-sm font-bold text-stone-900">{formattedAuditDate}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Corporate Decline - 12M Evil Score Trend */}
          <Card className="border border-stone-300 shadow-sm bg-white overflow-hidden rounded-sm group transition-all hover:border-stone-400">
            <div className="bs-header flex flex-col sm:flex-row sm:items-center justify-between border-b border-stone-200 bg-stone-50/50 p-4 gap-2">
              <CardTitle className="text-sm font-bold text-stone-950 flex items-center gap-2 m-0 font-heading">
                <TrendingUp className="w-4 h-4 text-red-600 animate-pulse" />
                Corporate Accountability Decline Trend (12M Evil Score)
              </CardTitle>
              <div className="flex items-center gap-2.5">
                <div className="text-[10px] font-mono bg-red-50 text-red-800 border border-red-200 px-2 py-0.5 rounded-sm font-bold uppercase tracking-wide">
                  Escalating Risk Profile
                </div>
                <Button
                  variant="outline"
                  size="icon-xs"
                  className="h-6 w-6 border-stone-200 text-stone-500 hover:text-stone-900 shadow-sm rounded-none"
                  title="Expand Chart"
                  onClick={() => setExpandedChart("trend")}
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <CardContent className="p-6 bg-white space-y-4">
              <p className="text-xs text-stone-600 leading-relaxed">
                An upward trajectory in the Biofail Accountability Index indicates escalating environmental infractions, worsening carbon-intensity anomalies, persistent toxic emissions spikes, and aggressive anti-regulation lobbying efforts over the trailing 12-month window.
              </p>
              <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={evilScoreHistory} margin={{ top: 10, right: 15, left: -25, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis 
                      dataKey="month" 
                      fontSize={11} 
                      fontWeight="600" 
                      axisLine={{ stroke: '#f1f5f9' }} 
                      tickLine={false} 
                      tick={{ fill: '#64748b' }} 
                    />
                    <YAxis 
                      domain={[0, 100]}
                      fontSize={11} 
                      fontWeight="600" 
                      axisLine={{ stroke: '#f1f5f9' }} 
                      tickLine={false} 
                      tick={{ fill: '#64748b' }} 
                    />
                    <Tooltip 
                      cursor={{ stroke: '#f1f5f9', strokeWidth: 1 }}
                      contentStyle={{ 
                        backgroundColor: '#ffffff', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '4px', 
                        fontSize: '12px', 
                        fontWeight: '600', 
                        padding: '8px 12px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                      itemStyle={{ color: '#dc2626', padding: 0 }}
                      labelStyle={{ color: '#000', marginBottom: '2px', fontSize: '9px' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="score" 
                      stroke="#dc2626" 
                      strokeWidth={3} 
                      activeDot={{ r: 6, stroke: '#fca5a5', strokeWidth: 2 }}
                      dot={{ r: 3, stroke: '#dc2626', strokeWidth: 1, fill: '#ffffff' }}
                      animationDuration={1500}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Company Data Narrative */}
          <div className="space-y-6 pt-10 border-t border-stone-100">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-center gap-4 mb-2 flex-1">
                <h3 className="text-xl font-bold text-stone-950">Company Profile</h3>
                <div className="h-px flex-1 bg-stone-100" />
              </div>
              
              <Button 
                onClick={handleAnalyze} 
                disabled={isAnalyzing}
                className="bg-stone-800 hover:bg-stone-900 border border-stone-600 text-stone-50 font-bold text-sm h-10 px-6 gap-2 shrink-0 shadow-sm rounded-sm"
              >
                {isAnalyzing ? (
                  <div className="flex items-center gap-2">
                    <Activity className="w-3 h-3 animate-pulse" />
                    Generating Report...
                  </div>
                ) : (
                  <>
                    <FileText className="w-3.5 h-3.5" />
                    AI Impact Analysis
                  </>
                )}
              </Button>
            </div>

            <AnimatePresence>
              {analysis && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="bg-stone-950 text-stone-50 p-8 rounded-lg border-l-4 border-destructive relative overflow-hidden"
                >
                  <div className="relative z-10 space-y-6">
                    <div className="flex items-center gap-3 mb-4 text-sm font-bold text-destructive">
                      <div className="w-2.5 h-2.5 rounded-full bg-destructive animate-pulse" />
                      <span>Classified AI Analysis</span>
                    </div>
                    <div className="prose prose-stone prose-invert max-w-none text-stone-300 font-bold prose-p:leading-relaxed prose-headings:font-bold prose-headings:text-white prose-hr:border-stone-800 markdown-body">
                      <Markdown>{analysis.analysis}</Markdown>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <p className="text-stone-700 leading-relaxed text-sm font-medium max-w-4xl">
              {company.description || "Detailed profile data is currently being integrated for this entity."}
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              {company.tags.map(tag => (
                <Badge key={tag} variant="secondary" className="text-xs font-semibold px-3 py-1 bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors border border-stone-200 rounded-full">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          {/* Active Compliance Ultimatums & Intervention Drafting Desk */}
          {(() => {
            const ultimatums = getSectorUltimatums(company.sector, company.companyName);
            const activeUltimatumText = selectedUltimatumId === "all"
              ? ultimatums.map(u => `- [${u.id}] ${u.title}: ${u.target}`).join("\n")
              : (() => {
                  const u = ultimatums.find(item => item.id === selectedUltimatumId);
                  return u ? `- [${u.id}] ${u.title}: ${u.target}` : "";
                })();

            const draftLetterText = `CIVIC INQUIRY: DEMAND FOR ENVIRONMENTAL COMPLIANCE & RESTITUTION
To: ${selectedExec}
Corporate Command Registry, ${company.companyName}
RE: Outstanding ESG Compliance Targets & Environmental Impact Cost Liabilities

This letter serves to bring severe civic concerns regarding ESG and planetary environmental liabilities associated with ${company.companyName}'s operations to your board's immediate attention.

Public environmental filings, regulatory logs, and independent carbon databases list the following ecological impact profile for your current operating term:
1. Annual carbon emissions load: ${company.co2 || 0} Mt CO2eq (estimated at $${((company.co2 || 0) * 195).toFixed(1)}M USD in uncompensated environmental damages and warming costs).
2. Volatile organic/methane emissions: ${company.methane || 0} kt CH4.
3. Lobbying to fines ratio: Your corporation spent $${((company.lobbyingSpend || 0) / 1000000).toFixed(1)}M USD on lobbying actions, outspending environmental fines by a ratio of ${lobbyingMultiplier.toFixed(1)}x.

WE RESPECTFULLY URGE BOARD INTERVENTION ON THE FOLLOWING COMPLIANCE TARGET CORRIDORS:
${activeUltimatumText}

Long-term ecological preservation is an essential pillar of corporate responsibility and civic trust. We request a documented, public response outlining concrete steps being taken to resolve these targets before their respective deadlines.

Sincerely,
Concerned Citizens & Biofail Coalition (Registry #${company.id.toString().padStart(5, '0')})`;

            return (
              <div className="space-y-6 pt-10 border-t border-stone-200 mt-8">
                <div className="flex items-center gap-3">
                  <ShieldAlert className="w-5 h-5 text-stone-700" />
                  <h3 className="text-xl font-bold text-stone-950">Pending Environmental Targets</h3>
                  <div className="h-px flex-1 bg-stone-200" />
                </div>
                
                <p className="text-sm font-medium text-stone-600 leading-relaxed max-w-3xl">
                  We track critical environmental milestones that sector leaders have yet to verify as completed. Select a target below to automatically load its parameters into the draft advocacy letter below.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {ultimatums.map((u) => {
                    const isSelected = selectedUltimatumId === u.id;
                    return (
                      <div 
                        key={u.id}
                        onClick={() => setSelectedUltimatumId(isSelected ? "all" : u.id)}
                        className={cn(
                          "cursor-pointer p-5 rounded-lg border transition-all text-left space-y-4 shadow-sm h-full flex flex-col justify-between group",
                          isSelected 
                            ? "border-stone-800 bg-stone-50/20 ring-1 ring-stone-800/10 font-bold" 
                            : "border-stone-300 bg-white hover:border-stone-500 hover:shadow-md font-bold"
                        )}
                      >
                        <div className="space-y-2">
                          <div className="flex justify-between items-start gap-2">
                            <span className="font-mono text-xs font-bold text-stone-500">{u.id}</span>
                            <Badge className={cn(
                              "text-[9px] font-bold px-1.5 py-0.5 rounded-md leading-none border shrink-0",
                              u.status === "CRITICAL NON-COMPLIANCE" ? "border-red-200 bg-red-50 text-red-700" :
                              u.status === "FAILED TO COMPLY" ? "border-stone-900 bg-stone-950 text-white" :
                              "border-amber-200 bg-amber-50 text-amber-700"
                            )}>
                              {u.status}
                            </Badge>
                          </div>
                          <h4 className="text-sm font-bold text-stone-950 leading-tight group-hover:text-red-700 transition-colors">{u.title}</h4>
                          <p className="text-xs text-stone-600 leading-relaxed font-semibold">"{u.description}"</p>
                        </div>

                        <div className="pt-4 border-t border-stone-100 space-y-2">
                          <div>
                            <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider block">Target Standard</span>
                            <span className="text-xs font-bold text-stone-900 leading-snug block">{u.target}</span>
                          </div>
                          <div className="flex justify-between items-center text-[10px] pt-1">
                            <span className="font-bold text-red-600">Risk: {u.risk}</span>
                            <span className="font-mono text-stone-400 font-bold">Due: {u.deadline}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Visual Direct Advocacy Intervention - Plea from Biofail */}
                <div className="border border-stone-250 bg-stone-50 p-6 rounded-lg space-y-6 mt-8 relative overflow-hidden shadow-xs">
                  {/* Visual accent blocks */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />
                  <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-red-500/5 rounded-full blur-2xl pointer-events-none" />
                  
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-stone-200">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="inline-block px-2 py-0.5 bg-red-100 text-red-800 border border-red-200 font-mono text-[9px] font-bold tracking-wider uppercase rounded-md">
                          IMMEDIATE ACTION
                        </span>
                        <span className="text-xs font-mono font-bold text-stone-500 uppercase tracking-widest">
                          BIOFAIL INTERVENTION NO. {1000 + company.id}
                        </span>
                      </div>
                      <h4 className="text-xl font-bold tracking-tight text-stone-900 flex items-center gap-2 font-sans">
                        Plea from Biofail: Retribute Planetary Debt
                      </h4>
                    </div>
                    <div className="flex items-center gap-1.5 self-start sm:self-center font-mono text-xs bg-stone-100 border border-stone-200 px-3 py-1.5 rounded-md">
                      <Users className="w-3.5 h-3.5 text-stone-600" />
                      <span className="font-bold text-stone-800">
                        {((company.id * 1057) % 4000 + 1200 + (signedPetition ? 1 : 0)).toLocaleString()}
                      </span>
                      <span className="text-stone-500">Committed Citizens</span>
                    </div>
                  </div>

                  {/* Ecological Deficit Scorecard Banner */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-stone-900 text-white p-5 border border-stone-800 shadow-sm animate-fade-in rounded-lg">
                    <div className="md:col-span-2 space-y-1">
                      <span className="text-[10px] font-mono font-black text-amber-400 tracking-wider uppercase block">
                        Uncompensated Biophysical Deficit
                      </span>
                      <div className="text-3xl font-mono font-black tracking-tighter text-white">
                        ${(damageValuationUsd).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                      <p className="text-xs text-stone-400 font-bold leading-tight">
                        Annual externalized damages to climate, water systems, and local biodiversities.
                      </p>
                    </div>
                    <div className="flex flex-col justify-center border-t md:border-t-0 md:border-l border-white/10 pt-4 md:pt-0 md:pl-5 space-y-1">
                      <span className="text-[10px] font-mono font-bold text-stone-400 uppercase tracking-wider block">
                        Political Leverage Protection
                      </span>
                      <div className="text-lg font-bold text-red-400">
                        {lobbyingMultiplier.toFixed(1)}x Over fine limits
                      </div>
                      <p className="text-[11px] text-stone-400 font-bold leading-none">
                        Lobbying spend outpaces registered environmental fines.
                      </p>
                    </div>
                  </div>

                  {/* Dynamic Target Selection inside the Editorial layout */}
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <label className="text-xs font-bold text-stone-700 uppercase tracking-wider flex items-center gap-1.5 shrink-0">
                        <Users className="w-3.5 h-3.5 text-stone-500" /> Direct Plea To Executive:
                      </label>
                      <select 
                        value={selectedExec}
                        onChange={(e) => setSelectedExec(e.target.value)}
                        className="bg-white border border-stone-300 text-stone-900 text-xs font-semibold py-1.5 px-3 rounded-md outline-none focus:border-stone-500 transition-colors cursor-pointer shadow-xs"
                      >
                        {company.executives?.map((exec: any, idx: number) => (
                          <option key={idx} value={exec.name}>{exec.name} ({exec.role})</option>
                        ))}
                        <option value="the Board of Directors">The Full Board of Directors</option>
                      </select>
                    </div>

                    {/* Highly stylized Parchment-Like Open Letter From Biofail */}
                    <div className="bg-[#FAF9F5] border border-amber-900/15 p-6 relative shadow-inner rounded-md">
                      {/* Subtitle / Letterhead decoration */}
                      <div className="absolute top-2 right-4 text-[9px] font-mono text-amber-800/40 tracking-widest font-bold">
                        OPEN MEMORANDUM / DEC {new Date().getFullYear()}
                      </div>
                      <div className="text-stone-750 font-serif leading-relaxed text-sm md:text-base space-y-4 max-w-none">
                        <p className="font-bold border-b border-stone-200/60 pb-3 text-stone-900 font-sans tracking-tight">
                          TO: {selectedExec}
                          <br />
                          CC: Board of Executive Advisors, Shareholders, and Endangered Ecosystems
                        </p>
                        <p className="indent-8 italic text-stone-800">
                          "This memo serves as an urgent planetary plea from the citizens supporting Biofail. Your business operations at <strong className="font-sans text-stone-950 font-extrabold">{company.companyName}</strong> generate an uncompensated planetary footprint representing an estimated <strong className="font-sans text-stone-950 font-black">${(damageValuationUsd / 1000000).toFixed(0)}M USD in annual externalized planetary damage</strong>."
                        </p>
                        <p className="indent-8 italic text-stone-800">
                          "This deficit is supported by a <strong className="font-sans text-stone-950 font-extrabold">${(company.lobbyingSpend || 0).toLocaleString()} USD</strong> lobbying strategy deployed to influence sustainability monitoring. This political shield outpaces the environmental fines settled by your corporation by a ratio of <span className="font-mono text-stone-950 font-bold">{lobbyingMultiplier.toFixed(1)} to 1</span>."
                        </p>
                        <p className="border-t border-stone-200/60 pt-4 font-bold text-stone-900 tracking-tight font-sans">
                          OUR ACTIVE COMMUNITY RESTITUTION DEMANDS:
                        </p>
                        
                        <div className="font-sans text-xs space-y-2 pt-1">
                          {ultimatums.map((u, i) => (
                            <div key={u.id} className="flex gap-2.5 items-start bg-stone-100 p-3 border border-stone-200 rounded-md">
                              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-stone-300 text-stone-800 text-[10px] font-bold flex items-center justify-center font-mono">
                                {i + 1}
                              </span>
                              <div className="space-y-0.5">
                                <span className="font-bold text-stone-900">{u.title}</span>
                                <p className="text-[11px] text-stone-600 font-medium leading-tight">{u.target} — {u.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>

                        <p className="indent-8 italic text-stone-800 pt-3">
                          "We ask that you immediately halt political capture spending, comply with all active emission mitigation directives, and allocate corresponding financial reserves directly to biological restitution efforts."
                        </p>
                        <p className="pt-3 border-t border-dotted border-stone-300 font-mono text-xs text-stone-500 font-bold font-sans">
                          — Issued on behalf of the Biofail Planetary Restitution Coalition.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Fully functional interaction triggers */}
                  <div className="space-y-3.5 pt-4 border-t border-stone-200">
                    <div className="text-xs text-stone-600 font-medium bg-stone-100 px-3 py-2 rounded-md border border-stone-200 flex items-center justify-between gap-3">
                      {signedPetition ? (
                        <div className="flex items-center gap-2 text-emerald-850 font-semibold">
                          <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                          <span>Escalation active: Your signature has been verified and registered.</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-stone-700 font-semibold">
                          <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0" />
                          <span>Plea Pending: Require your signature to authorize public pressure dossier.</span>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-2.5 w-full">
                      {!signedPetition ? (
                        <Button
                          size="sm"
                          className="px-3 text-xs bg-red-50 hover:bg-red-100/80 text-red-700 border border-red-200 rounded-md font-semibold h-10 shadow-xs tracking-tight flex items-center justify-center gap-1.5 transition-all"
                          onClick={handleSignPetition}
                        >
                          <FilePenLine className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">Sign Open Letter</span>
                        </Button>
                      ) : (
                        <div className="px-3 py-2 bg-emerald-50 text-emerald-800 text-xs font-semibold border border-emerald-200 rounded-md flex items-center justify-center gap-1.5 h-10">
                          <Check className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">Active</span>
                        </div>
                      )}

                      <Button
                        size="sm"
                        variant="outline"
                        className="px-3 text-xs bg-stone-100 hover:bg-stone-200 text-stone-800 border border-stone-300 rounded-md font-semibold h-10 flex items-center justify-center gap-1.5 transition-all"
                        onClick={() => {
                          const markdownDossier = `### BIOFAIL PLANETARY RESTITUTION DOSSIER
**Target Entity:** ${company.companyName}
**To:** ${selectedExec}

**ECOLOGICAL IMPACTS SUMMARY:**
* Uncompensated Annual Planetary Damages: $${damageValuationUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })} USD
* Political Lobbying Investment Protection: $${(company.lobbyingSpend || 0).toLocaleString()} USD
* Political/Fine Outspend Ratio: ${lobbyingMultiplier.toFixed(1)}x

**COMMUNITY DEMANDS CATALOGUE:**
${ultimatums.map((u, i) => `${i + 1}. [${u.id}] ${u.title}: ${u.target} — ${u.description}`).join('\n')}

We demand immediate remediation accountability. Open letter signed by citizens worldwide on Biofail.`;
                          handleCopyDemand(markdownDossier);
                        }}
                      >
                        {copiedDemand ? (
                          <>
                            <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                            <span className="truncate">Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3.5 h-3.5 text-stone-500 shrink-0" />
                            <span className="truncate">Copy Dossier</span>
                          </>
                        )}
                      </Button>

                      <Button
                        size="sm"
                        className="px-3 text-xs bg-stone-100 hover:bg-stone-200 text-stone-800 border border-stone-300 rounded-md font-semibold h-10 flex items-center justify-center gap-1.5 transition-all"
                        asChild
                      >
                        <a
                          href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Holding ${company.companyName} and CEO ${selectedExec} accountable for $${(damageValuationUsd / 1000000).toFixed(0)}M in ecological damages. See the restitution details on Biofail! @biofail_coalition`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Twitter className="w-3.5 h-3.5 text-stone-500 shrink-0" />
                          <span className="truncate">Share Protest</span>
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* New Surveillance Activity Log */}
          <div className="space-y-4 pt-10 border-t border-stone-200 mt-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-stone-900">
                <History className="w-4 h-4 text-stone-600" />
                <h3 className="text-base font-bold text-stone-900">Audit & Activity History</h3>
              </div>
            </div>
 
            <div className="border border-stone-300 rounded-sm overflow-hidden bg-white shadow-sm">
              <div className="grid grid-cols-12 bg-stone-100 border-b border-stone-200 p-3 text-sm font-bold text-stone-600">
                <div className="col-span-2">Timestamp</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-8 px-2">Observation / Log Entry</div>
              </div>
              <div className="divide-y divide-stone-200">
                {(company.activityLog || [
                  { date: "2024-05-12", title: "Emissions spike detected", type: "Anomaly", log: "Satellite telemetry indicated unauthorized methane flaring at primary extraction site." },
                  { date: "2024-03-28", title: "Regulatory fine issued", type: "Enforcement", log: "Environmental protection agency assessed $18.4M fine for groundwater contamination." },
                  { date: "2024-01-15", title: "Metrics Update", type: "Data_sync", log: "Revised annual metrics integrated into Biofail Index. Emissions intensity increased by 4%." },
                  { date: "2023-11-20", title: "Supply Chain Investigation", type: "Probe", log: "Independent investigation into primary forest loss in Southeast Asia initiated." }
                ]).map((event, i) => (
                  <div key={i} className="grid grid-cols-12 p-4 text-sm hover:bg-stone-50 transition-colors items-start">
                    <div className="col-span-2 font-mono text-xs text-stone-500 mt-0.5">{event.date}</div>
                    <div className="col-span-2">
                       <span className={cn(
                        "text-xs font-bold px-2 py-0.5 rounded-sm border leading-none",
                        event.type === "Anomaly" ? "border-red-200 bg-red-50 text-red-700" :
                        event.type === "Enforcement" ? "border-orange-200 bg-orange-50 text-orange-700" :
                        "border-stone-200 bg-stone-50 text-stone-600"
                      )}>{event.type}</span>
                    </div>
                    <div className="col-span-8 px-2 space-y-1">
                      <div className="font-bold text-stone-900 leading-tight">{event.title}</div>
                      <div className="text-stone-600 leading-relaxed">"{event.log}"</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Geographic Footprint */}
          <div className="space-y-6 pt-8">
            <div className="flex items-center gap-4">
              <Globe className="w-5 h-5 text-stone-600" />
              <h3 className="text-xl font-bold text-stone-950">Geographic Impact</h3>
              <div className="h-px flex-1 bg-stone-200" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {company.locations?.map((loc, idx) => (
                <div key={idx} className="bg-stone-50 p-4 rounded-sm border border-stone-200 flex justify-between items-center shadow-sm hover:border-stone-400 transition-colors">
                  <div className="space-y-1">
                    <div className="text-xs font-bold text-stone-500">Region</div>
                    <div className="text-sm font-bold text-stone-900">{loc.country}</div>
                  </div>
                  <Badge className={cn(
                    "text-xs font-bold rounded-sm h-5",
                    loc.impact === "High" ? "bg-stone-900 text-white" : 
                    loc.impact === "Medium" ? "bg-stone-600 text-white" : "bg-stone-200 text-stone-800"
                  )}>
                    {loc.impact} Impact
                  </Badge>
                </div>
              ))}
            </div>
          </div>

          {/* Corporate Transgressions & Media Watchdog Dossier */}
          <div className="space-y-8 pt-10 border-t border-stone-200 mt-8">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-stone-700" />
              <h3 className="text-xl font-bold text-stone-950">Environmental Violations & Case Documentation</h3>
              <div className="h-px flex-1 bg-stone-200" />
            </div>

            <div className="space-y-6">
              {/* Regulatory Compliance Timeline */}
              <div className="bg-stone-50 border border-stone-200 p-6 rounded-none space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <h4 className="text-sm font-black uppercase tracking-wider text-stone-900 flex items-center gap-2">
                      <History className="w-4 h-4 text-red-600" />
                      Regulatory Compliance Timeline
                    </h4>
                    <p className="text-xs text-stone-600 leading-relaxed">
                      Vertical audit stepper tracking major ESG violation events, subsequent government interventions, and current resolution statuses.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-[10px] font-mono font-bold uppercase tracking-wider text-stone-500">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-emerald-100" /> Resolved
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-500 ring-2 ring-amber-100" /> Pending
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-red-100" /> Ongoing
                    </span>
                  </div>
                </div>

                <div className="relative pl-6 sm:pl-8 space-y-8 before:absolute before:top-2 before:bottom-2 before:left-[11px] sm:before:left-[15px] before:w-0.5 before:bg-stone-200 z-0">
                  {complianceTimelineEvents.map((evt, idx) => {
                    const isResolved = evt.status === "resolved";
                    const isPending = evt.status === "pending";
                    const dotColor = isResolved 
                      ? "bg-emerald-500 ring-emerald-100 border-emerald-600" 
                      : isPending 
                        ? "bg-amber-500 ring-amber-100 border-amber-600" 
                        : "bg-red-500 ring-red-100 border-red-600";
                    const badgeColor = isResolved 
                      ? "bg-emerald-50 text-emerald-800 border-emerald-200" 
                      : isPending 
                        ? "bg-amber-50 text-amber-800 border-amber-200" 
                        : "bg-red-50 text-red-800 border-red-200";

                    return (
                      <div key={idx} className="relative flex flex-col md:flex-row md:gap-8 items-start group/step">
                        {/* Dot indicator aligned with vertical line */}
                        <div className="absolute left-[-19px] sm:left-[-23px] top-1.5 flex items-center justify-center">
                          <div className={cn(
                            "w-4 h-4 rounded-full ring-4 transition-all duration-300 group-hover/step:scale-125 border-2 bg-white",
                            isResolved ? "border-emerald-500 ring-emerald-50" : isPending ? "border-amber-500 ring-amber-50" : "border-red-500 ring-red-50"
                          )}>
                            <div className={cn("w-1.5 h-1.5 rounded-full m-auto mt-[3px]", isResolved ? "bg-emerald-500" : isPending ? "bg-amber-500" : "bg-red-500")} />
                          </div>
                        </div>

                        {/* Year Indicator on left for md+ screens */}
                        <div className="md:w-20 shrink-0 mb-1 md:mb-0 font-mono text-sm font-black text-stone-900 leading-none pt-1">
                          {evt.year}
                        </div>

                        {/* Content Card */}
                        <div className="flex-1 bg-white border border-stone-200 hover:border-stone-400 p-5 space-y-3 transition-all rounded-sm shadow-xs">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <h5 className="text-sm font-black text-stone-950 leading-snug">
                              {evt.title}
                            </h5>
                            <span className={cn(
                              "inline-block text-[9px] font-mono font-extrabold uppercase tracking-wide border px-1.5 py-0.5 rounded-sm",
                              badgeColor
                            )}>
                              {evt.statusText}
                            </span>
                          </div>

                          <p className="text-xs text-stone-600 leading-relaxed">
                            {evt.description}
                          </p>

                          {/* Government intervention details */}
                          <div className="pt-3 border-t border-stone-150 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                            <div className="flex items-center gap-1.5 font-bold text-stone-500">
                              <span className="font-mono">Intervention:</span>
                              <span className="text-stone-850 font-sans">{evt.resolutionDate}</span>
                            </div>
                            {evt.detail && (
                              <div className="text-[11px] text-stone-500 font-semibold italic bg-stone-50 border border-stone-100 px-2 py-1 rounded-sm leading-tight">
                                {evt.detail}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Scandals Audit Logs */}
              {company.scandals && company.scandals.length > 0 && (
                <div className="space-y-4">
                  <div className="text-xs font-bold uppercase text-stone-500 tracking-wider">Verified Enforcement Cases & Violations</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {company.scandals.map((scan: any, idx: number) => (
                      <div key={idx} className="p-5 rounded-none border border-stone-300 bg-white shadow-sm hover:border-stone-400 hover:shadow-md transition-all flex flex-col justify-between space-y-4">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-xs font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-sm">Case Year: {scan.year}</span>
                            <span className="font-mono text-[10px] font-bold text-stone-400 select-all">Case ID: BF-SCAN-{company.id}-{idx + 1}</span>
                          </div>
                          <h4 className="text-sm font-bold text-stone-950 leading-tight">{scan.title}</h4>
                          <p className="text-xs text-stone-600 font-semibold leading-relaxed">"{scan.description}"</p>
                        </div>
                        <div className="pt-3 border-t border-stone-100 flex flex-wrap gap-2 justify-between items-center">
                          <span className="text-[10px] font-bold text-stone-500">Source: Public Audit Reports</span>
                          <div className="flex gap-2">
                            {scan.url && (
                              <a 
                                href={scan.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center h-6 px-2.5 text-[9px] font-extrabold border border-red-300 rounded-none bg-red-50 hover:bg-red-100/80 text-red-700 hover:text-red-900 transition-colors"
                              >
                                <ExternalLink className="w-2.5 h-2.5 mr-1 text-red-500" />
                                Official Dossier
                              </a>
                            )}
                            <Link 
                              to={`/report?company=${encodeURIComponent(company.companyName)}`}
                              className="inline-flex items-center justify-center h-6 px-2.5 text-[9px] font-extrabold border border-stone-300 rounded-none bg-stone-50 hover:bg-stone-100 text-stone-700 hover:text-stone-900 transition-colors"
                            >
                              <FileText className="w-2.5 h-2.5 mr-1 text-stone-500" />
                              File Report
                            </Link>
                            <Link 
                              to={`/?sector=${encodeURIComponent(company.sector)}`}
                              className="inline-flex items-center justify-center h-6 px-2.5 text-[9px] font-extrabold border border-stone-300 rounded-none bg-stone-50 hover:bg-stone-100 text-stone-700 hover:text-stone-900 transition-colors"
                            >
                              <Layers className="w-2.5 h-2.5 mr-1 text-stone-500" />
                              Audit Sector
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* News Articles */}
              <div className="space-y-4">
                <div className="text-xs font-bold uppercase text-stone-500 tracking-wider">Investigative Press Records</div>
                <div className="space-y-4">
                  {company.recentNews?.map((news, idx) => (
                    <div key={idx} className="p-5 rounded-none border border-stone-300 bg-white hover:border-stone-400 hover:shadow-sm transition-all shadow-sm space-y-4">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-stone-500">{news.date}</span>
                          <span className="text-[10px] font-bold text-stone-400 bg-stone-100 px-2 py-0.5 border border-stone-200 uppercase rounded-sm leading-none">{news.source}</span>
                        </div>
                        <span className="text-[10px] font-mono text-stone-400">Media Record</span>
                      </div>
                      {news.url ? (
                        <a 
                          href={news.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="group/headline block text-left"
                        >
                          <h4 className="text-base font-bold text-stone-950 leading-tight leading-snug group-hover/headline:text-red-700 group-hover/headline:underline cursor-pointer flex items-center gap-1.5 hover:text-red-700 transition-colors">
                            {news.headline}
                            <ExternalLink className="w-4 h-4 text-stone-400 shrink-0 inline-block align-middle group-hover/headline:text-red-600 transition-colors" />
                          </h4>
                        </a>
                      ) : (
                        <h4 className="text-base font-bold text-stone-950 leading-tight leading-snug">{news.headline}</h4>
                      )}
                      
                      <div className="pt-3 border-t border-stone-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <span className="text-[11px] font-semibold text-stone-600 leading-snug max-w-xl">
                          This entry represents relevant investigative journalism. Cross-reference independent climate databases to review absolute ESG metrics.
                        </span>
                        <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-auto">
                          <Link 
                            to={`/?region=${encodeURIComponent(company.region)}`}
                            className="inline-flex items-center justify-center h-7 px-3 text-[10px] font-extrabold border border-stone-300 bg-white hover:bg-stone-50 text-stone-700 hover:text-stone-900 transition-colors rounded-none"
                          >
                            <Globe className="w-3 h-3 mr-1 text-stone-500" />
                            Regional Registry
                          </Link>
                          <Link 
                            to={`/report?company=${encodeURIComponent(company.companyName)}`}
                            className="inline-flex items-center justify-center h-7 px-3 text-[10px] font-extrabold border border-stone-300 bg-white hover:bg-stone-50 text-stone-700 hover:text-stone-900 transition-colors rounded-none"
                          >
                            <ShieldAlert className="w-3 h-3 mr-1 text-stone-500" />
                            Report Press Leak
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Area */}
        <div className="lg:col-span-4 space-y-8">
          <Card className="border-stone-200 shadow-sm overflow-hidden">
            <CardHeader className="bg-stone-50 border-b border-stone-200 py-3">
              <CardTitle className="text-xs font-bold text-stone-600">Industry Benchmark</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <BenchmarkItem 
                label="Carbon Intensity" 
                value={Math.floor((company.totalEmissions / 100) * 15)} 
                labelSuffix="ABOVE AVG" 
              />
              <BenchmarkItem 
                label="Methane Leakage" 
                value={Math.floor((company.methane / 50) * 12)} 
                labelSuffix="ABOVE AVG" 
              />
              <BenchmarkItem 
                label="Audit Transparency" 
                value={Math.floor(-10 - (company.violations / 2))} 
                labelSuffix="BELOW AVG" 
                isNegative
              />
              
              <div className="pt-6 border-t border-stone-100">
                <div className="text-xs font-bold text-stone-600 mb-3">Peer Group: {company.sector}</div>
                <div className="flex flex-wrap gap-x-3 gap-y-2">
                  {getPeers(company.sector).map((peerName, idx) => {
                    const peerC = localCompanies.find(c => c.companyName.toLowerCase() === peerName.toLowerCase());
                    const href = peerC ? `/${peerC.slug}` : `/?search=${encodeURIComponent(peerName)}`;
                    return (
                      <Link 
                        key={idx}
                        to={href}
                        onClick={() => {
                          if (peerC) onSelectCompany(peerC);
                        }}
                        className="text-xs font-bold text-stone-600 hover:text-stone-900 hover:underline transition-all"
                      >
                        {peerName}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone-200 shadow-sm overflow-hidden">
            <CardHeader className="bg-stone-50 border-b border-stone-200 py-3 flex flex-row items-center justify-between">
              <CardTitle className="text-xs font-bold text-stone-600 m-0 leading-none">Energy Consumption Mix</CardTitle>
              <Button
                variant="outline"
                size="icon-xs"
                className="h-6 w-6 border-stone-200 text-stone-500 hover:text-stone-900 shadow-sm rounded-none"
                title="Expand Chart"
                onClick={() => setExpandedChart("energy")}
              >
                <Maximize2 className="w-3.5 h-3.5" />
              </Button>
            </CardHeader>
            <CardContent className="p-6">
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={company.energyMix}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="percentage"
                    >
                      {company.energyMix?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={
                          entry.type === "Renewables" ? "#10b981" : 
                          entry.type === "Coal" ? "#1a1a1a" : 
                          entry.type === "Gas" ? "#3b82f6" : "#f59e0b"
                        } />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                {company.energyMix?.map((entry, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 
                      entry.type === "Renewables" ? "#10b981" : 
                      entry.type === "Coal" ? "#1a1a1a" : 
                      entry.type === "Gas" ? "#3b82f6" : "#f59e0b"
                    }} />
                    <span className="text-xs font-bold text-stone-600">{entry.type} {entry.percentage}%</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone-200 shadow-sm overflow-hidden">
            <CardHeader className="bg-stone-50 border-b border-stone-200 py-3">
              <CardTitle className="text-xs font-bold text-stone-600">Corporate Structure</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-xs font-bold text-stone-500">
                  <Layers className="w-3 h-3" />
                  Key Subsidiaries
                </div>
                <div className="flex flex-wrap gap-2">
                  {company.subsidiaries?.map(sub => (
                    <Badge key={sub} variant="outline" className="text-xs font-bold border-stone-200 text-stone-600">
                      {sub}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-xs font-bold text-stone-500">
                  <Users className="w-3 h-3" />
                  Leadership
                </div>
                <div className="space-y-3">
                  {company.executives?.map((exec, idx) => (
                    <div key={idx} className="flex justify-between items-center text-xs">
                      <span className="font-bold text-stone-950">{exec.name}</span>
                      <span className="text-stone-600">{exec.role}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone-200 shadow-sm overflow-hidden">
            <CardHeader className="bg-stone-50 border-b border-stone-200 py-3">
              <CardTitle className="text-xs font-bold text-stone-600">Data Integrity Profile</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-stone-100">
                <IntegrityRow label="ESG Rating" value={`${company.esgScore}/100`} />
                <IntegrityRow label="Confidence" value="High (Verified)" color="text-stone-900" />
                <IntegrityRow label="Last Audit" value={formattedAuditDate} />
                <IntegrityRow label="Source" value="Regulatory Filings" />
              </div>
              <div className="p-6 bg-stone-50/50">
                <Link 
                  to="/methodology"
                  className="inline-flex items-center justify-center w-full gap-2 text-sm font-bold h-11 border border-stone-300 bg-white hover:bg-stone-50 text-stone-700 hover:text-stone-900 transition-colors rounded-none"
                >
                  <FileText className="w-3.5 h-3.5 text-stone-500" />
                  Audit Methodology
                </Link>
              </div>
            </CardContent>
          </Card>

          <div className="bg-stone-100 border border-stone-200 rounded-sm p-6 space-y-4">
            <div className="flex items-center gap-3 text-stone-900">
              <ShieldAlert className="w-5 h-5 text-stone-600" />
              <h4 className="text-sm font-bold">Risk Advisory</h4>
            </div>
            <p className="text-sm font-bold text-stone-600 leading-relaxed">
              This entity is currently flagged for high ecological risk. Continued investment or partnership may result in significant reputational damage.
            </p>
          </div>

          <a 
            href="https://www.evercove.com"
            target="_blank"
            rel="noopener noreferrer"
            className="block group relative overflow-hidden bg-gradient-to-br from-[#0B0B0C] to-[#16161B] border border-stone-800 rounded-lg p-6 space-y-5 hover:border-emerald-500/40 transition-all duration-300 shadow-xl cursor-pointer"
          >
            {/* Subtle premium background glow effect */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-2xl pointer-events-none group-hover:bg-emerald-500/10 transition-colors duration-300" />
            
            <div className="flex items-center justify-between">
              <div className="w-12 h-12 rounded-full bg-white p-0.5 flex items-center justify-center overflow-hidden shrink-0 border border-stone-700 group-hover:scale-105 transition-transform duration-300">
                <CompanyLogo companyName="Evercove" domain="evercove.com" size="lg" className="border-0 bg-white" />
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[9px] font-mono font-bold tracking-wider uppercase rounded-full">
                  OFFICIAL SPONSOR
                </span>
                <span className="text-[10px] text-stone-500 font-mono flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  ACTIVE INDEXING
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <h4 className="text-lg font-bold text-white tracking-tight flex items-center gap-1.5 m-0 group-hover:text-emerald-400 transition-colors duration-200">
                  Evercove
                  <ExternalLink className="w-3.5 h-3.5 text-stone-500 group-hover:text-emerald-400 transition-colors" />
                </h4>
                <p className="text-xs text-emerald-500/90 font-semibold tracking-wide uppercase mt-0.5">
                  Sustainable Live Experiences. Zero Compromise.
                </p>
              </div>
              
              <p className="text-xs text-stone-400 leading-relaxed font-medium">
                The premier partner for brands and event organizers seeking next-gen carbon neutral gathers, zero-waste supply chains, and transparent impact audits.
              </p>

              {/* Mini Features Checklist */}
              <div className="pt-2 grid grid-cols-1 gap-1.5 border-t border-stone-800/80">
                <div className="flex items-center gap-2 text-[11px] text-stone-300 font-bold">
                  <span className="text-emerald-500 font-mono">✦</span>
                  <span>Carbon-Neutral Gatherings</span>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-stone-300 font-bold">
                  <span className="text-emerald-500 font-mono">✦</span>
                  <span>Zero-Waste Circularity</span>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-stone-300 font-bold">
                  <span className="text-emerald-500 font-mono">✦</span>
                  <span>Audited Brand Experiences</span>
                </div>
              </div>
            </div>

            <div 
              className="w-full bg-white text-stone-950 hover:bg-emerald-400 hover:text-stone-950 text-xs font-bold h-10 rounded-sm flex items-center justify-center gap-2 transition-all duration-200 shadow-md font-mono uppercase tracking-wider"
            >
              Collaborate With Evercove
            </div>
          </a>
        </div>
      </div>

      {/* Modal for Interactive Audit History */}
      <Dialog open={expandedChart === "audit"} onOpenChange={(open) => setExpandedChart(open ? "audit" : null)}>
        <DialogContent className="sm:max-w-4xl w-full max-w-4xl p-6 md:p-8 max-h-[90vh] overflow-y-auto bg-white border border-stone-200 shadow-2xl rounded-none">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-xl font-bold text-stone-900 flex items-center gap-2 font-heading">
              <span className="w-2.5 h-2.5 rounded-full bg-red-600 animate-pulse" />
              Interactive Environmental Threat Audit History
            </DialogTitle>
            <DialogDescription className="text-sm text-stone-500 font-semibold">
              Trailing 12-Month Audited Threat Index for {company.companyName}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Expanded Chart */}
            <div className="h-80 w-full relative bg-stone-50 rounded-none border border-stone-200 p-4 shadow-inner flex flex-col justify-between">
              <div className="w-full h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart 
                    data={evilScoreHistory} 
                    margin={{ top: 10, right: 10, left: -20, bottom: 4 }}
                  >
                    <defs>
                      <linearGradient id="colorExpandedAudit" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#dc2626" stopOpacity={0.25}/>
                        <stop offset="95%" stopColor="#dc2626" stopOpacity={0.02}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" opacity={0.6} />
                    <XAxis 
                      dataKey="month" 
                      fontSize={11} 
                      fontWeight="600" 
                      axisLine={{ stroke: '#f1f5f9' }} 
                      tickLine={false} 
                      tick={{ fill: '#64748b' }} 
                    />
                    <YAxis 
                      domain={[0, 100]}
                      fontSize={11} 
                      fontWeight="600" 
                      axisLine={{ stroke: '#f1f5f9' }} 
                      tickLine={false} 
                      tick={{ fill: '#64748b' }} 
                    />
                    <ReferenceLine 
                      y={80} 
                      stroke="#ef4444" 
                      strokeDasharray="3 3" 
                      strokeWidth={1.5}
                      opacity={0.8} 
                    />
                    <Tooltip 
                      cursor={{ stroke: '#dc2626', strokeWidth: 1.5, strokeDasharray: '3 3' }}
                      contentStyle={{ 
                        backgroundColor: '#ffffff', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '0px', 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        padding: '10px 14px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="score" 
                      stroke="#dc2626" 
                      strokeWidth={3} 
                      fillOpacity={1} 
                      fill="url(#colorExpandedAudit)"
                      dot={{ r: 5, fill: '#dc2626', strokeWidth: 2, stroke: '#ffffff' }}
                      activeDot={{ r: 8, strokeWidth: 2, stroke: '#ffffff', fill: '#991b1b' }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-between w-full text-[10px] font-mono text-stone-500 px-1 uppercase tracking-wider leading-none mt-2 pt-2 border-t border-stone-200/50">
                <span className="font-bold text-stone-600">{evilScoreHistory[0].month}</span>
                <span className="text-stone-400 font-sans tracking-widest font-bold">12-MONTH ENVIRONMENTAL THREAT AXIS</span>
                <span className="font-bold text-stone-600">{evilScoreHistory[11].month}</span>
              </div>
              
              {/* Highly visual non-overlapping chart legend inside expanded modal */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[10px] font-mono font-bold text-stone-500 px-1 pt-1.5">
                <div className="flex items-center gap-1.5">
                  <span className="w-3.5 h-0.5 bg-red-600 inline-block"></span>
                  <span>Audit Score Trajectory</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3.5 h-0.5 border-t-2 border-dashed border-red-500 inline-block"></span>
                  <span>Critical Threshold Indicator (80% Alarm Level)</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-red-650 rounded-full inline-block"></span>
                  <span>Evaluation Point</span>
                </div>
              </div>
            </div>

            {/* Detailed Analysis Section */}
            <div className="space-y-3">
              <h3 className="text-xs font-bold text-stone-900 uppercase tracking-wider text-left">Detailed Monthly Audit Trail</h3>
              <div className="border border-stone-200 rounded-none overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[600px]">
                  <thead>
                    <tr className="bg-stone-50 border-b border-stone-200">
                      <th className="p-3 text-xs font-bold text-stone-600 uppercase tracking-wider w-24">Month</th>
                      <th className="p-3 text-xs font-bold text-stone-600 uppercase tracking-wider w-32">Threat Score</th>
                      <th className="p-3 text-xs font-bold text-stone-600 uppercase tracking-wider">Audited Infraction & Regulatory Log Record</th>
                      <th className="p-3 text-xs font-bold text-stone-600 uppercase tracking-wider text-right w-44">Assessed Penalty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-150">
                    {evilScoreHistory.map((h, idx) => {
                      const getEstPenalty = (score: number) => {
                        if (score >= 95) return `$${(score * 250000).toLocaleString()}`;
                        if (score >= 85) return `$${(score * 125000).toLocaleString()}`;
                        return `$${(score * 35000).toLocaleString()}`;
                      };
                      const getScoreColor = (score: number) => {
                        if (score >= 90) return "bg-red-100 text-red-800 border-red-200";
                        if (score >= 80) return "bg-amber-100 text-amber-800 border-amber-200";
                        return "bg-stone-100 text-stone-800 border-stone-200";
                      };

                      return (
                        <tr key={idx} className="hover:bg-stone-50/50 transition-colors">
                          <td className="p-3 text-sm font-bold font-mono text-stone-700">{h.month}</td>
                          <td className="p-3">
                            <span className={cn("inline-flex items-center px-2 py-0.5 rounded-none text-xs font-bold border", getScoreColor(h.score))}>
                              {h.score}%
                            </span>
                          </td>
                          <td className="p-3 text-sm text-stone-700 font-medium text-left">
                            {h.volatilityEffect || "Baseline tracking and compliance monitoring check. No unique infractions reported."}
                          </td>
                          <td className="p-3 text-sm font-bold font-mono text-stone-900 text-right">
                            {h.score >= 80 ? getEstPenalty(h.score) : "Compliance Verified"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal for Emissions Record */}
      <Dialog open={expandedChart === "emissions"} onOpenChange={(open) => setExpandedChart(open ? "emissions" : null)}>
        <DialogContent className="sm:max-w-4xl w-full max-w-4xl p-6 md:p-8 max-h-[90vh] overflow-y-auto bg-white border border-stone-200 shadow-2xl rounded-none">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-xl font-bold text-stone-900 flex items-center gap-2 font-heading">
              <Activity className="w-5 h-5 text-indigo-600" />
              Corporate Carbon Emissions Audit Log
            </DialogTitle>
            <DialogDescription className="text-sm text-stone-500 font-semibold">
              Verified historical CO2e greenhouse gas emissions tracking (2015-2024)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Expanded Chart */}
            <div className="h-80 w-full relative bg-stone-50 rounded-none border border-stone-200 p-4 shadow-inner flex flex-col justify-between">
              <div className="w-full h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={company.historicalEmissions} margin={{ top: 10, right: 10, left: -20, bottom: 4 }}>
                    <defs>
                      <linearGradient id="colorExpandedEmissions" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.15}/>
                        <stop offset="95%" stopColor="#4f46e5" stopOpacity={0.01}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" opacity={0.6} />
                    <XAxis 
                      dataKey="year" 
                      fontSize={11} 
                      fontWeight="600" 
                      axisLine={{ stroke: '#f1f5f9' }} 
                      tickLine={false} 
                      tick={{ fill: '#64748b' }} 
                    />
                    <YAxis 
                      fontSize={11} 
                      fontWeight="600" 
                      axisLine={{ stroke: '#f1f5f9' }} 
                      tickLine={false} 
                      tick={{ fill: '#64748b' }} 
                    />
                    <Tooltip 
                      cursor={{ stroke: '#e2e8f0', strokeWidth: 1 }}
                      contentStyle={{ 
                        backgroundColor: '#ffffff', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '0px', 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        padding: '10px 14px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#4f46e5" 
                      strokeWidth={3} 
                      fillOpacity={1} 
                      fill="url(#colorExpandedEmissions)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-between w-full text-[10px] font-mono text-stone-500 px-1 uppercase tracking-wider leading-none mt-2 pt-2 border-t border-stone-200/50">
                <span className="font-bold text-stone-600">2015</span>
                <span className="text-stone-400 font-sans tracking-widest font-bold">HISTORICAL CARBON EMISSIONS TIMELINE</span>
                <span className="font-bold text-stone-600">2024</span>
              </div>
            </div>

            {/* Detailed Analysis Section */}
            <div className="space-y-3">
              <h3 className="text-xs font-bold text-stone-900 uppercase tracking-wider text-left">Annual Emissions Breakdown & YoY Analytics</h3>
              <div className="border border-stone-200 rounded-none overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[600px]">
                  <thead>
                    <tr className="bg-stone-50 border-b border-stone-200">
                      <th className="p-3 text-xs font-bold text-stone-600 uppercase tracking-wider">Year</th>
                      <th className="p-3 text-xs font-bold text-stone-600 uppercase tracking-wider">Emissions (Mt CO2e)</th>
                      <th className="p-3 text-xs font-bold text-stone-600 uppercase tracking-wider">YoY Change (%)</th>
                      <th className="p-3 text-xs font-bold text-stone-600 uppercase tracking-wider">Compliance Classification</th>
                      <th className="p-3 text-xs font-bold text-stone-600 uppercase tracking-wider text-right">Est. Carbon Tax Liability</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-150">
                    {(() => {
                      const sorted = [...company.historicalEmissions].sort((a, b) => a.year - b.year);
                      return sorted.map((item, idx) => {
                        const prev = sorted[idx - 1];
                        const yoy = prev ? ((item.value - prev.value) / prev.value) * 100 : null;
                        
                        const getRating = (val: number | null) => {
                          if (val === null) return { text: "Baseline Year", class: "bg-stone-100 text-stone-800 border-stone-200" };
                          if (val <= -4) return { text: "Eco-Progressive", class: "bg-emerald-100 text-emerald-800 border-emerald-200" };
                          if (val <= 0) return { text: "Marginal Progress", class: "bg-teal-100 text-teal-800 border-teal-200" };
                          if (val <= 3) return { text: "Worsening Trend", class: "bg-amber-100 text-amber-800 border-amber-200" };
                          return { text: "Critical Escalation", class: "bg-red-100 text-red-800 border-red-200" };
                        };

                        const rating = getRating(yoy);
                        const taxLiability = item.value * 8500000; // $8.5M per Mt of CO2e carbon tax rate

                        return (
                          <tr key={idx} className="hover:bg-stone-50/50 transition-colors">
                            <td className="p-3 text-sm font-bold font-mono text-stone-700">{item.year}</td>
                            <td className="p-3 text-sm font-bold text-stone-900">{item.value.toLocaleString()} Mt</td>
                            <td className="p-3 text-sm font-mono font-bold">
                              {yoy !== null ? (
                                <span className={yoy > 0 ? "text-red-600" : "text-emerald-600"}>
                                  {yoy > 0 ? "▲" : "▼"} {Math.abs(yoy).toFixed(1)}%
                                </span>
                              ) : (
                                <span className="text-stone-400">--</span>
                              )}
                            </td>
                            <td>
                              <span className={cn("inline-flex items-center px-2 py-0.5 rounded-none text-xs font-bold border", rating.class)}>
                                {rating.text}
                              </span>
                            </td>
                            <td className="p-3 text-sm font-bold font-mono text-stone-900 text-right">
                              ${taxLiability.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                            </td>
                          </tr>
                        );
                      }).reverse();
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal for Accountability Trend */}
      <Dialog open={expandedChart === "trend"} onOpenChange={(open) => setExpandedChart(open ? "trend" : null)}>
        <DialogContent className="sm:max-w-4xl w-full max-w-4xl p-6 md:p-8 max-h-[90vh] overflow-y-auto bg-white border border-stone-200 shadow-2xl rounded-none">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-xl font-bold text-stone-900 flex items-center gap-2 font-heading">
              <TrendingUp className="w-5 h-5 text-red-600" />
              Trailing 12-Month Accountability Volatility Audit
            </DialogTitle>
            <DialogDescription className="text-sm text-stone-500 font-semibold">
              Statistical non-compliance, Standard Deviation (σ), and historical risk progression
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Expanded Chart */}
            <div className="h-80 w-full relative bg-stone-50 rounded-none border border-stone-200 p-4 shadow-inner flex flex-col justify-between">
              <div className="w-full h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={evilScoreHistory} margin={{ top: 10, right: 15, left: -25, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" opacity={0.6} />
                    <XAxis 
                      dataKey="month" 
                      fontSize={11} 
                      fontWeight="600" 
                      axisLine={{ stroke: '#f1f5f9' }} 
                      tickLine={false} 
                      tick={{ fill: '#64748b' }} 
                    />
                    <YAxis 
                      domain={[0, 100]}
                      fontSize={11} 
                      fontWeight="600" 
                      axisLine={{ stroke: '#f1f5f9' }} 
                      tickLine={false} 
                      tick={{ fill: '#64748b' }} 
                    />
                    <Tooltip 
                      cursor={{ stroke: '#f1f5f9', strokeWidth: 1 }}
                      contentStyle={{ 
                        backgroundColor: '#ffffff', 
                        border: '1px solid #e2e8f0', 
                        borderRadius: '0px', 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        padding: '10px 14px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="score" 
                      stroke="#dc2626" 
                      strokeWidth={3} 
                      activeDot={{ r: 7, stroke: '#fca5a5', strokeWidth: 2 }}
                      dot={{ r: 4, stroke: '#dc2626', strokeWidth: 1.5, fill: '#ffffff' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-between w-full text-[10px] font-mono text-stone-500 px-1 uppercase tracking-wider leading-none mt-2 pt-2 border-t border-stone-200/50">
                <span className="font-bold text-stone-600">{evilScoreHistory[0].month}</span>
                <span className="text-stone-400 font-sans tracking-widest font-bold">12-MONTH NON-COMPLIANCE TREND</span>
                <span className="font-bold text-stone-600">{evilScoreHistory[11].month}</span>
              </div>
            </div>

            {/* Volatility Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-left font-sans">
              <div className="bg-stone-50 p-4 border border-stone-200 rounded-none">
                <div className="text-xs font-bold text-stone-500 mb-1 uppercase">Volatility Index</div>
                <div className="text-xl font-bold font-mono text-stone-900">{volatilityMetrics.stdDev}σ</div>
                <div className="text-[10px] text-stone-400 mt-1 font-semibold leading-normal">Standard deviation of score fluctuation</div>
              </div>
              <div className="bg-stone-50 p-4 border border-stone-200 rounded-none">
                <div className="text-xs font-bold text-stone-500 mb-1 uppercase">Avg Monthly Shift</div>
                <div className="text-xl font-bold font-mono text-stone-900">{volatilityMetrics.avgChange} pts</div>
                <div className="text-[10px] text-stone-400 mt-1 font-semibold leading-normal">Average absolute MoM shift</div>
              </div>
              <div className="bg-stone-50 p-4 border border-stone-200 rounded-none">
                <div className="text-xs font-bold text-stone-500 mb-1 uppercase">Total 12M Delta</div>
                <div className="text-xl font-bold font-mono text-stone-900">
                  <span className={volatilityMetrics.delta >= 0 ? "text-red-600" : "text-emerald-600"}>
                    {volatilityMetrics.deltaPrefix}{volatilityMetrics.delta}%
                  </span>
                </div>
                <div className="text-[10px] text-stone-400 mt-1 font-semibold leading-normal">Baseline to current change</div>
              </div>
              <div className="bg-stone-50 p-4 border border-stone-200 rounded-none">
                <div className="text-xs font-bold text-stone-500 mb-1 uppercase">Risk Trajectory</div>
                <div className="text-xl font-bold text-stone-900">
                  {volatilityMetrics.delta >= 0 ? "Escalating" : "Stable/Improving"}
                </div>
                <div className="text-[10px] text-stone-400 mt-1 font-semibold leading-normal">Trajectory behavior class</div>
              </div>
            </div>

            {/* Breakdown Details */}
            <div className="space-y-3">
              <h3 className="text-xs font-bold text-stone-900 uppercase tracking-wider text-left">Audited Behavior Progression</h3>
              <div className="border border-stone-200 rounded-none overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[600px]">
                  <thead>
                    <tr className="bg-stone-50 border-b border-stone-200">
                      <th className="p-3 text-xs font-bold text-stone-600 uppercase tracking-wider w-32">Month</th>
                      <th className="p-3 text-xs font-bold text-stone-600 uppercase tracking-wider w-40">Accountability Score</th>
                      <th className="p-3 text-xs font-bold text-stone-600 uppercase tracking-wider">MoM Change</th>
                      <th className="p-3 text-xs font-bold text-stone-600 uppercase tracking-wider text-right">Violation Intensity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-150">
                    {evilScoreHistory.map((h, idx) => {
                      const prev = evilScoreHistory[idx - 1];
                      const mom = prev ? h.score - prev.score : null;
                      const getIntensity = (score: number) => {
                        if (score >= 90) return "High Risk (Tier 1 Surveillance)";
                        if (score >= 75) return "Warning Level (Tier 2 Surveillance)";
                        return "Standard Tracking";
                      };

                      return (
                        <tr key={idx} className="hover:bg-stone-50/50 transition-colors">
                          <td className="p-3 text-sm font-bold font-mono text-stone-700">{h.month}</td>
                          <td className="p-3 text-sm font-bold text-stone-900">{h.score}%</td>
                          <td className="p-3 text-sm font-mono font-bold text-left">
                            {mom !== null ? (
                              <span className={mom > 0 ? "text-red-600" : mom < 0 ? "text-emerald-600" : "text-stone-400"}>
                                {mom > 0 ? `+${mom}` : mom < 0 ? `${mom}` : "0"}
                              </span>
                            ) : (
                              <span className="text-stone-400">Baseline</span>
                            )}
                          </td>
                          <td className="p-3 text-sm font-semibold text-stone-600 text-right">
                            {getIntensity(h.score)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal for Energy Mix */}
      <Dialog open={expandedChart === "energy"} onOpenChange={(open) => setExpandedChart(open ? "energy" : null)}>
        <DialogContent className="sm:max-w-4xl w-full max-w-4xl p-6 md:p-8 max-h-[90vh] overflow-y-auto bg-white border border-stone-200 shadow-2xl rounded-none">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-xl font-bold text-stone-900 flex items-center gap-2 font-heading">
              <Zap className="w-5 h-5 text-yellow-500" />
              Energy Consumption Mix Breakdown
            </DialogTitle>
            <DialogDescription className="text-sm text-stone-500 font-semibold">
              Audited distribution of power inputs, ecological class, and transition timelines
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Expanded Chart */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center bg-stone-50 border border-stone-200 rounded-none p-6 shadow-inner">
              <div className="h-64 flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={company.energyMix}
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="percentage"
                    >
                      {company.energyMix?.map((entry: any, index: number) => (
                        <Cell key={`expanded-cell-${index}`} fill={
                          entry.type === "Renewables" ? "#10b981" : 
                          entry.type === "Coal" ? "#1a1a1a" : 
                          entry.type === "Gas" ? "#3b82f6" : "#f59e0b"
                        } />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="space-y-4">
                <h4 className="text-xs font-bold text-stone-800 uppercase tracking-wider text-left">Current Power Mix Legend</h4>
                <div className="grid grid-cols-1 gap-3">
                  {company.energyMix?.map((entry: any, index: number) => {
                    const getColor = (type: string) => {
                      if (type === "Renewables") return "bg-emerald-500";
                      if (type === "Coal") return "bg-stone-900";
                      if (type === "Gas") return "bg-blue-500";
                      return "bg-amber-500";
                    };
                    const getImpact = (type: string) => {
                      if (type === "Renewables") return "Zero direct emissions; critical grid transition component.";
                      if (type === "Coal") return "Highest CO2/sulfur output; key driver of localized soot.";
                      if (type === "Gas") return "Methane flaring risks; high heat, high emission factor.";
                      return "Moderate carbon factor; requires carbon capture optimization.";
                    };

                    return (
                      <div key={index} className="flex gap-3 border border-stone-200/60 p-3 rounded-none bg-white text-left">
                        <span className={cn("w-3.5 h-3.5 rounded-full mt-0.5 shrink-0", getColor(entry.type))} />
                        <div className="space-y-0.5">
                          <span className="text-sm font-bold text-stone-900">{entry.type} • {entry.percentage}%</span>
                          <p className="text-xs text-stone-500 font-medium leading-relaxed">{getImpact(entry.type)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Regulatory Guidelines Section */}
            <div className="space-y-3">
              <h3 className="text-xs font-bold text-stone-900 uppercase tracking-wider text-left">Transition Audit & Target Milestones</h3>
              <div className="border border-stone-200 rounded-none overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[600px]">
                  <thead>
                    <tr className="bg-stone-50 border-b border-stone-200">
                      <th className="p-3 text-xs font-bold text-stone-600 uppercase tracking-wider">Energy Class</th>
                      <th className="p-3 text-xs font-bold text-stone-600 uppercase tracking-wider w-24">Percentage</th>
                      <th className="p-3 text-xs font-bold text-stone-600 uppercase tracking-wider">Ecological Classification</th>
                      <th className="p-3 text-xs font-bold text-stone-600 uppercase tracking-wider text-right">Target Transition by 2030</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-150">
                    {company.energyMix?.map((entry: any, index: number) => {
                      const getTransitionText = (type: string) => {
                        if (type === "Renewables") return "Increase to 100% Matching";
                        if (type === "Coal") return "100% Phase-out Mandate";
                        if (type === "Gas") return "75% Reduction / Transition";
                        return "Transition to Battery/Renewable Match";
                      };

                      const getClass = (type: string) => {
                        if (type === "Renewables") return "bg-emerald-100 text-emerald-800 border-emerald-200";
                        if (type === "Coal") return "bg-red-100 text-red-800 border-red-200";
                        return "bg-amber-100 text-amber-800 border-amber-200";
                      };

                      return (
                        <tr key={index} className="hover:bg-stone-50/50 transition-colors">
                          <td className="p-3 text-sm font-bold text-stone-900">{entry.type}</td>
                          <td className="p-3 text-sm font-bold font-mono text-stone-700">{entry.percentage}%</td>
                          <td className="p-3">
                            <span className={cn("inline-flex items-center px-2 py-0.5 rounded-none text-xs font-bold border", getClass(entry.type))}>
                              {entry.type === "Renewables" ? "Low Carbon Input" : entry.type === "Coal" ? "Critical Contaminant" : "Moderate Contaminant"}
                            </span>
                          </td>
                          <td className="p-3 text-sm font-bold text-stone-800 text-right">{getTransitionText(entry.type)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </motion.article>
    </TooltipProvider>
  );
}

function DetailStat({ label, value, icon: Icon, color, tooltipText, ariaValueText }: { label: string; value: string | number | React.ReactNode; icon: any; color?: string; tooltipText?: string; ariaValueText?: string }) {
  const cardVariants = {
    hidden: { opacity: 0, y: 15 },
    show: { opacity: 1, y: 0, transition: { type: "spring" as any, stiffness: 100, damping: 15 } }
  };

  const innerContent = (
    <div className="space-y-1 w-full text-left">
      <div className="flex items-center justify-between gap-1 text-sm font-bold text-stone-500 w-full">
        <div className="flex items-center gap-2">
          <Icon className={cn("w-4 h-4", color)} />
          {label}
        </div>
        {tooltipText && (
          <Info className="w-3.5 h-3.5 text-stone-400 opacity-60 group-hover:opacity-100 transition-opacity" />
        )}
      </div>
      <div className="text-xl font-bold text-black tabular-nums">{value}</div>
    </div>
  );

  const displayAriaValue = ariaValueText || (typeof value === "string" || typeof value === "number" ? value : "");

  if (tooltipText) {
    return (
      <motion.div variants={cardVariants} className="w-full">
        <UITooltip>
          <TooltipTrigger 
            data-slot="tooltip-trigger"
            data-stat-card
            tabIndex={0}
            role="group"
            aria-label={`${label}: ${displayAriaValue}. Description: ${tooltipText}`}
            className="bg-white p-4 rounded-sm border border-stone-200 shadow-sm group transition-all select-none hover:border-stone-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-500 focus-visible:border-stone-500 cursor-help block w-full text-left"
          >
            {innerContent}
          </TooltipTrigger>
          <TooltipContent className="bg-stone-950 text-white border border-stone-800 px-3 py-2 text-xs max-w-[240px] shadow-lg rounded font-sans font-medium" side="top">
            {tooltipText}
          </TooltipContent>
        </UITooltip>
      </motion.div>
    );
  }

  return (
    <motion.div 
      data-stat-card
      tabIndex={0}
      role="group"
      aria-label={`${label}: ${displayAriaValue}`}
      className="bg-white p-4 rounded-sm border border-stone-200 shadow-sm group transition-all select-none hover:border-stone-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-500 focus-visible:border-stone-500 block w-full text-left"
      variants={cardVariants}
    >
      {innerContent}
    </motion.div>
  );
}

function IntegrityRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="flex justify-between items-center p-4 px-4 sm:px-6 border-b border-stone-50 last:border-0 hover:bg-stone-50/50 transition-colors">
      <span className="text-sm font-bold text-stone-500">{label}</span>
      <span className={cn("text-base font-bold", color || "text-black")}>{value}</span>
    </div>
  );
}

function BenchmarkItem({ label, value, labelSuffix, isNegative }: { label: string; value: number; labelSuffix: string; isNegative?: boolean }) {
  const safeValue = isNaN(value) ? 0 : value;
  const displayValue = safeValue > 0 ? `+${safeValue}%` : `${safeValue}%`;
  const colorClass = (isNegative && safeValue < 0) || (!isNegative && safeValue > 0) ? "text-destructive" : "text-emerald-600";
  const bgClass = (isNegative && safeValue < 0) || (!isNegative && safeValue > 0) ? "bg-destructive" : "bg-emerald-500";
  
  return (
    <div className="space-y-3 px-1 group">
      <div className="flex justify-between items-end">
        <div className="flex items-center gap-2">
           <div className={cn("w-1.5 h-1.5", bgClass)} />
           <span className="text-xs font-bold text-stone-500">{label}</span>
        </div>
        <span className={cn("text-base font-bold tabular-nums", colorClass)}>
          {displayValue} <span className="text-xs opacity-70 font-bold">{labelSuffix}</span>
        </span>
      </div>
      <div className="relative h-1.5 w-full bg-stone-100 rounded-none overflow-hidden">
        <div className="absolute left-1/2 top-0 w-px h-full bg-stone-300 z-10" />
        <div 
          className={cn("absolute h-full transition-all duration-1000", bgClass)} 
          style={{ 
            left: safeValue > 0 ? '50%' : `${Math.max(0, 50 + safeValue / 2)}%`,
            width: `${Math.min(50, Math.abs(safeValue / 2))}%` 
          }} 
        />
      </div>
    </div>
  );
}

function getPeers(sector: string): string[] {
  switch (sector) {
    case "Oil & Gas": return ["ExxonMobil", "Chevron", "Shell", "BP", "TotalEnergies", "Saudi Aramco"];
    case "Mining": return ["Rio Tinto", "BHP", "Vale", "Glencore", "Anglo American", "Freeport-McMoRan"];
    case "Utilities": return ["NextEra Energy", "Duke Energy", "Enel", "EDF", "Shinra Electric Power", "Iberdrola"];
    case "Transport": return ["Maersk", "MSC", "FedEx", "UPS", "DHL", "Delta Air Lines"];
    case "Chemicals": return ["DuPont", "Dow Chemical", "BASF", "SABIC", "Sasol", "LG Chem"];
    case "Agriculture": return ["Cargill", "Archer Daniels Midland", "Bunge", "Bayer", "Nutrien", "Wilmar International"];
    case "Manufacturing": return ["ArcelorMittal", "Nippon Steel", "POSCO", "Baosteel", "ThyssenKrupp", "Foxconn"];
    case "Automotive": return ["Volkswagen", "Toyota", "General Motors", "Ford", "Tesla", "Hyundai"];
    case "Technology": return ["Apple", "Microsoft", "Google", "Amazon", "Meta", "Samsung"];
    case "Finance": return ["JPMorgan Chase", "Bank of America", "HSBC", "Goldman Sachs", "Citigroup", "BlackRock"];
    case "Retail": return ["Walmart", "Costco", "Target", "Home Depot", "H&M", "IKEA"];
    case "Pharmaceuticals": return ["Johnson & Johnson", "Pfizer", "Roche", "Novartis", "Merck", "AbbVie"];
    case "Consumer Goods": return ["Procter & Gamble", "Nestlé", "Unilever", "Coca-Cola", "PepsiCo", "Danone"];
    default: return ["Apple", "Walmart", "ExxonMobil", "JPMorgan Chase", "Amazon", "Microsoft"];
  }
}

function getSectorUltimatums(sector: string, companyName: string): {
  id: string;
  title: string;
  target: string;
  deadline: string;
  status: "CRITICAL NON-COMPLIANCE" | "FAILED TO COMPLY" | "PENDING REGULATORY AUDIT";
  risk: string;
  description: string;
}[] {
  switch (sector) {
    case "Oil & Gas":
      return [
        {
          id: "ULT-01",
          title: "Eliminate Sovereign Methane Flaring",
          target: "Zero continuous flare stack operation & direct methane emission capping.",
          deadline: "December 15, 2026",
          status: "CRITICAL NON-COMPLIANCE",
          risk: "Asset Seizure & Civil Injunctions",
          description: "Methane accounts for over 25% of overall warming load. Current thermal audits indicate continuous stack leakages."
        },
        {
          id: "ULT-02",
          title: "Cease Deepwater Exploration Sourcing",
          target: "Phase out ocean drilling reserves from the capital development pool.",
          deadline: "August 1, 2027",
          status: "FAILED TO COMPLY",
          risk: "Shareholder Divestment Directives",
          description: "Continued exploration locks in destructive extraction pipelines that violate the Paris 1.5°C threshold limit."
        },
        {
          id: "ULT-03",
          title: "Reform Executive Metric Ties",
          target: "Decouple executive board compensation from fossil output metrics.",
          deadline: "March 31, 2027",
          status: "PENDING REGULATORY AUDIT",
          risk: "Board Deselection Resolutions",
          description: "Internal compensation formulas reward raw extraction volume, actively sabotaging sustainability pledges."
        }
      ];
    case "Mining":
      return [
        {
          id: "ULT-01",
          title: "Neutralize Heavy Metal Tailing Streams",
          target: "Halt cyanide and mercury-submerged slag discharge into surrounding river basins.",
          deadline: "November 10, 2026",
          status: "CRITICAL NON-COMPLIANCE",
          risk: "State Watershed Revocation",
          description: "Satellite telemetry detects arsenic and sulfate runoffs exceeding acceptable clean-water thresholds by 400%."
        },
        {
          id: "ULT-02",
          title: "Fund Indigenous Restitution Pools",
          target: "Direct equitable transfers representing 12% of sector earnings to affected tribes.",
          deadline: "January 15, 2027",
          status: "FAILED TO COMPLY",
          risk: "Tribal Blockades & Multi-national Recalcitrance",
          description: "Vast mining concessions occupy historic lands, degrading water tables without restitution."
        },
        {
          id: "ULT-03",
          title: "Halt Critical Canopy Exploitation",
          target: "Commit to Zero deforestation in mineral extraction zones.",
          deadline: "September 30, 2026",
          status: "PENDING REGULATORY AUDIT",
          risk: "Global Supply Chain Embargoes",
          description: "Tonnage extraction requires massive topsoil stripping, displacing fragile old-growth flora ecosystems."
        }
      ];
    case "Agriculture":
      return [
        {
          id: "ULT-01",
          title: "Disclose Deforestation Sourcing Trails",
          target: "Provide public, satellite-verifiable supply chain logs showing zero canopy removal.",
          deadline: "December 1, 2026",
          status: "CRITICAL NON-COMPLIANCE",
          risk: "Deforestation Import Bans",
          description: "Suppliers in the Amazon, Cerrado, and Southeast Asia continue back-channel forest clearing to create feed concessions."
        },
        {
          id: "ULT-02",
          title: "Remediate Nitrogen Runoff Spikes",
          target: "Impose a 50% restriction on artificial nitrate fertilizers that feed coastal dead zones.",
          deadline: "July 12, 2026",
          status: "FAILED TO COMPLY",
          risk: "Hydrological Surcharge Assessments",
          description: "Fertilizer leachate is feeding mega-algal blooms that deplete sovereign oxygen and crush coastal fisheries."
        },
        {
          id: "ULT-03",
          title: "Enforce Water Stress Caps",
          target: "Reduce groundwater pumping by 40% in vulnerable, high-stress river aquifers.",
          deadline: "February 28, 2027",
          status: "PENDING REGULATORY AUDIT",
          risk: "Emergency Aquifer Pumping Bans",
          description: "Excessive industrial irrigation is drying domestic wells and threatening systemic food security."
        }
      ];
    default:
      return [
        {
          id: "ULT-01",
          title: "Halt Environmental Policy Lobbying",
          target: "De-fund or withdraw all political action committees undermining emission caps.",
          deadline: "October 10, 2026",
          status: "CRITICAL NON-COMPLIANCE",
          risk: "Lobbyist Registry Revocations",
          description: "The corporation funds back-door lobbying blocks to dilute environmental tracking and carbon tax metrics."
        },
        {
          id: "ULT-02",
          title: "Implement Social Damage Compensation",
          target: "Establish a direct matching resource recovery fund equal to annual ecosystem damages.",
          deadline: "March 1, 2027",
          status: "FAILED TO COMPLY",
          risk: "Citizen Liability Class Actions",
          description: "Operating margins ignore the public clean-up and healthcare burdens created by post-extraction residues."
        },
        {
          id: "ULT-03",
          title: "Transition to 100% On-grid Renewable Match",
          target: "Ensure peak operation matches clean energy grid-injection values.",
          deadline: "January 15, 2027",
          status: "PENDING REGULATORY AUDIT",
          risk: "High-emissions Grid Penalties",
          description: "Indirect emissions from dirty grids and unmitigated supply-chain manufacturing constitute massive secondary warming factors."
        }
      ];
  }
}

interface CountUpProps {
  end: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
}

export function CountUp({ end, duration = 1200, prefix = "", suffix = "", decimals = 0 }: CountUpProps) {
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    let startTime: number | null = null;
    const startValue = 0;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Easing function: easeOutQuad
      const easeProgress = progress * (2 - progress);
      const currentValue = startValue + easeProgress * (end - startValue);
      
      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(end); // Ensure exact final value is reached
      }
    };

    requestAnimationFrame(animate);
  }, [end, duration]);

  return (
    <span>
      {prefix}
      {count.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix}
    </span>
  );
}

